import { glifoDoSelo } from '@vestigo/core/area-gamificacao';
import { ProvedorDeIdioma, useLocale, useT } from './lib/idioma.js';
import { marcaDaArea } from '@vestigo/core/area-marca';
import { Suspense, lazy, useCallback, useEffect, useId, useState } from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Cabecalho } from './componentes/Cabecalho.js';
import { MolduraDaEntrada } from './entrada/Moldura.js';
import { EsqueciASenha, SecundarioDoEntrar } from './entrada/PecasDoEntrar.js';
import { FaixaDeSuporte } from './componentes/FaixaDeSuporte.js';
import { PortaoDosTermos } from './componentes/PortaoDosTermos.js';
import { CercaDeErro } from './componentes/CercaDeErro.js';
import { RolarAoTopo } from './componentes/RolarAoTopo.js';
import { AvisoDoCursoTrancado } from './componentes/CursoTrancado.js';
import { AvisoDoLancamento } from './componentes/Lancamento.js';
import { ReguasDaTranca } from './componentes/TrancaDeOrdem.js';
import { destinoDaTranca, lerTranca } from './lib/tranca.js';
import { fraseDaEspera, lerLancamento, quantoFalta, diasAte } from './lib/lancamento.js';
import { GlifoDeCadeado, GlifoDoTipo } from './lib/glifo-do-tipo.js';
import { rotuloDoTipo } from './lib/tipo-de-aula.js';
import { idsDeSimulado } from './lib/simulado.js';
import { useMedicaoDaPagina } from './lib/medicao.js';
/* O favicon, o `og:image` e o título da aba — e o desembrulho da marca, que
   mora num lugar só (`@vestigo/core/area-marca`). Ver `marca-no-documento.ts`. */
import { useMarca } from './lib/marca-no-documento.js';
import { PastilhaDoEncontro, PortasDaArea } from './componentes/PortasDaArea.js';
import { PaginaDeMinhasAulas } from './rotas/MinhasAulas.js';
import { SemEscrita } from './componentes/SemEscrita.js';
import { Vitrine } from './componentes/Vitrine.jsx';
import { telaComDicionario } from './lib/tela-com-dicionario.js';
import {
  api,
  ErroDaApi,
  fraseDoCadeado,
  type Aula,
  type AulaDaLista,
  type CursoDaVitrine,
  type CursoDetalhado,
  type Eu,
  type MeuProgresso,
  type ModuloDoCurso,
  type TelaDeEntrada,
  type TrilhaDetalhada,
  type Vitrine as DadosDaVitrine,
} from './lib/api.js';
import { useCarregar } from './lib/carregar.js';
import { destinoGuardado, enderecoDeEntrar } from './lib/destino.js';
import { emSessaoDeSuporte } from './lib/suporte.js';
import { duracaoLegivel, variaveisDoTema } from './lib/tema.js';
import { posicaoInicial } from './lib/posicao-inicial.js';
import {
  AREA_DO_HOST,
  caminhoDaArea,
  rotaDaArea,
  slugDaEntrada,
  useAreaSlug,
} from './lib/endereco.js';
import { temaSemeado } from './lib/semente.js';

/**
 * A sala de aula, num pedaço próprio do bundle.
 *
 * ## Por que ela é a única tela carregada sob demanda
 *
 * Porque ela é a maior e não é a primeira. A vitrine é a porta da área — é o que
 * abre em todo link que o dono manda —, e ela não precisa da lista lateral, das
 * abas de anotação, do contador de avanço nem da tela de parabéns para desenhar
 * um cartaz. Deixar tudo isso no pedaço inicial faria quem abre a vitrine no 4G
 * pagar por uma sala em que talvez nem entre nesta visita.
 *
 * O corte é seguro porque o limite é uma **rota**: quando a pessoa clica numa
 * aula, o pedaço já está sendo buscado, e o `Suspense` abaixo mostra a mesma
 * frase que a tela mostraria enquanto busca os dados da aula de qualquer jeito.
 * Ninguém espera duas vezes.
 *
 * É a mesma técnica que `useModoEstudio` já usa para a ponte do Estúdio, e pelo
 * mesmo motivo declarado lá: o aluno não paga por uma tela que ele não abriu.
 */
/**
 * ── O BOLETIM SAI DA PORTA (01/10/2026), E FOI ESCOLHIDO POR MEDIÇÃO ─────────
 *
 * A §15 da cerca de bytes deixou a folga da entrada em **148 B**, e escreveu que
 * a próxima obra não caberia sem CORTAR. Este é o corte, e ele foi escolhido
 * medindo quatro candidatos, não adivinhando:
 *
 * | o que foi posto em `lazy()` | entrada | contra os 334.852 B |
 * |---|---|---|
 * | os quatro da página do curso | 332.558 B | −2.294 B |
 * | só os três CONDICIONAIS | **335.689 B** | **+837 B — SUBIU** |
 * | **só o `RendimentoDoCurso`** | **331.721 B** | **−3.131 B** |
 *
 * **O resultado do meio é a lição.** Os três que eu escolheria por intuição —
 * `AvisoDoCursoTrancado`, `AvisoDoLancamento` e `ReguasDaTranca`, que só
 * desenham no caso raro — fizeram a porta CRESCER: eles partilham dependências
 * com o que fica, então nada saiu do grafo, e o que entrou foram três invólucros
 * de `lazy()`. *Um pedaço raro não é, por isso, um pedaço separável.*
 *
 * O que de facto pesa é o boletim: 9,9 kB de fonte que são só dele — ele importa
 * um `Link`, um tipo e uma função de endereço, e mais nada.
 *
 * ## O que custa, e por que é aceitável
 *
 * Ele desenha SEMPRE na página do curso, abaixo da lista inteira de aulas — e
 * por isso quase sempre abaixo da dobra. A espera é a mesma do `Certificado`,
 * que está três linhas abaixo dele e já é `lazy()` desde que nasceu. Trocar
 * «aparece com a página» por «aparece um instante depois, no fim de uma lista
 * longa» é o preço de 3.131 B na porta que TODA visita paga.
 */
const RendimentoDoCurso = telaComDicionario(() =>
  import('./componentes/RendimentoDoCurso.js').then((m) => ({ default: m.RendimentoDoCurso })),
);

const SalaDeAula = telaComDicionario(() =>
  import('./componentes/SalaDeAula.js').then((m) => ({ default: m.SalaDeAula })),
);

/*
 * ── AS OUTRAS TELAS QUE NÃO SÃO A PORTA (leva 0 da bancada da vitrine, 04/09/2026)
 *
 * «Ela é a única tela carregada sob demanda» deixou de ser verdade — e deixou
 * de ser suficiente. V13 orça o PEDAÇO DE ENTRADA, e ele media 366,55 kB no
 * `vite build` de 04/09/2026 (+45,95 kB sobre a base de 320,6; o teto é +40),
 * com o mural, a trilha, a agenda, o certificado e a prévia do Estúdio dentro
 * dele — telas que quem abre a vitrine talvez nunca veja nesta visita.
 *
 * Cada uma vira um pedaço próprio pela MESMA regra da sala: o corte é uma rota
 * (ou um bloco que só existe sob uma condição, como o certificado), e o
 * `Suspense` mostra a frase que a tela mostraria enquanto busca os dados.
 * A cerca `o-pedaco-de-entrada-so-carrega-a-porta.test.ts` impede que alguma
 * delas volte a ser importada por cima.
 */
const PaginaDaComunidade = telaComDicionario(() =>
  import('./rotas/Comunidade.js').then((m) => ({ default: m.PaginaDaComunidade })),
);
const PaginaDoPost = telaComDicionario(() =>
  import('./rotas/Comunidade.js').then((m) => ({ default: m.PaginaDoPost })),
);
/* O popup vem do MESMO módulo e do mesmo `import()`: é a mesma conversa noutra
   moldura, e `V13` não paga um byte por esta linha. */
const PopupDoPost = telaComDicionario(() =>
  import('./rotas/Comunidade.js').then((m) => ({ default: m.PopupDoPost })),
);
/* O popup do encontro (27/09/2026). Mesmo módulo do popup do post: quem abre um
   encontro está na comunidade, então o pedaço já está carregado — `V13` não paga
   nada por esta linha. */
const PopupDoEncontro = telaComDicionario(() =>
  import('./rotas/Comunidade.js').then((m) => ({ default: m.PopupDoEncontro })),
);
/*
 * ── A PORTA DO TUTOR, E ELA NÃO É UM OITAVO PEDAÇO (`Q10`) ──────────────────
 *
 * O mesmo módulo, o mesmo `import()`, o MESMO componente `Escrever`. É a ordem
 * do dono à letra — *«postar usando o mesmo editor dos alunos, para ele ter a
 * mesma experiência do aluno»* — e é por isso que ela é uma exportação de
 * `Comunidade.tsx` e não uma tela nova: **há UM compositor nesta casa**, e
 * duplicá-lo aqui seria repetir, do lado do aluno, exatamente a segunda
 * superfície que a revogação de `f3bca00a` acabou de matar do lado do painel.
 *
 * `V13` não paga nada por esta linha: o módulo já era um dos sete `lazy()`, e
 * quem abre esta rota é o tutor — o aluno nunca lhe chega, porque nada na área
 * dele aponta para cá.
 */
const PaginaDeEscrever = telaComDicionario(() =>
  import('./rotas/Comunidade.js').then((m) => ({ default: m.PaginaDeEscrever })),
);
/*
 * O perfil vive no MESMO modulo da comunidade, de proposito.
 *
 * Ele nao e uma oitava tela: e a mesma — quem edita o apelido veio da conversa,
 * e o `import()` que ja foi baixado serve as duas sem uma requisicao a mais.
 * Um `lazy()` proprio criaria um pedaco de 2 kB com o custo de rede de um de
 * 200, e a cerca `o-pedaco-de-entrada-so-carrega-a-porta` conta os modulos, nao
 * as telas.
 */
const PaginaDoPerfil = telaComDicionario(() =>
  import('./rotas/Comunidade.js').then((m) => ({ default: m.PaginaDoPerfil })),
);
const PaginaDaAgenda = telaComDicionario(() =>
  import('./rotas/Agenda.js').then((m) => ({ default: m.PaginaDaAgenda })),
);
/*
 * ── A PÁGINA DAS TRILHAS (06/10/2026) — e por que ela é `lazy()` ────────────
 *
 * A seção das trilhas saiu da vitrine (ordem do dono de 06/10: *«na vitrine sai
 * o espaço especial de trilhas já que ela terá sua própria prateleira»*), e com
 * ela morreu o `#trilhas` para onde a porta do cabeçalho apontava. O destino
 * passa a ser esta rota.
 *
 * Sob demanda pelo mesmo argumento da agenda e de minhas aulas: **ela não é a
 * primeira.** A porta da área é a vitrine, e o pedaço de entrada tem 779 B de
 * folga contra o teto de `V13` — uma página com lista, pistas e estado vazio não
 * cabe lá, e não tem por que caber. Ver `rotas/Trilhas.tsx`.
 */
const PaginaDasTrilhas = telaComDicionario(() =>
  import('./rotas/Trilhas.js').then((m) => ({ default: m.PaginaDasTrilhas })),
);
const PortaoDoInstagram = telaComDicionario(() => import('./componentes/PortaoDoInstagram.js'));

const Previa = telaComDicionario(() =>
  import('./rotas/Previa.js').then((m) => ({ default: m.Previa })),
);
const Cadastro = telaComDicionario(() => import('./rotas/Cadastro.js'));
/*
 * ── A PÁGINA PÚBLICA DE PROMOÇÃO DE UM CURSO (`0461`, 02/10/2026) ───────────
 *
 * Sob demanda pelo MESMO motivo do cadastro, e o motivo é mais forte aqui: quem
 * abre este endereço nunca passou pelo `/entrar` nem pela vitrine — ele vem de um
 * anúncio, muitas vezes no telefone, muitas vezes com a rede má. Empacotá-la por
 * cima faria toda a base de alunos que já comprou pagar, em todo login, a tela
 * de quem ainda não ouviu falar do tutor.
 *
 * O endereço é `rotaDaArea('/oferta/:cursoSlug')` — e NÃO `/promo/…` — porque é o
 * endereço que o painel já imprime ao tutor, ao lado do interruptor «Página de
 * venda» (`pages/cliente/areas/Curso.tsx`). Até esta leva ele caía no `path="*"`
 * e levava à raiz: o que o tutor copiava não chegava a página nenhuma.
 *
 * O NOME é `Promo` e não `Oferta`: `componentes/Oferta.tsx` já existe e é a tela
 * de DENTRO, a de quem está na área e não tem o curso. Duas peças com o mesmo
 * nome em duas camadas é a confusão que custa uma leva a desfazer.
 */
const Promo = telaComDicionario(() => import('./rotas/Promo.js'));
/**
 * ── A PÁGINA PÚBLICA DE PROMOÇÃO DE UMA TRILHA (`0475`, 19/10/2026) ─────────
 *
 * Ordem do dono: *«implemente a pagina de promocao para a trilha»*.
 *
 * **O MESMO pedaço** que a do curso — `./rotas/Promo.js` —, e não um `lazy()`
 * para outro ficheiro: as duas telas são o mesmo desenho, e quem abre uma nunca
 * abre a outra na mesma visita. O que ela carrega a mais é um componente de
 * trinta linhas. Ver o cabeçalho de `PromoDaTrilha`.
 *
 * O endereço é `/oferta/trilha/:trilhaSlug` — três segmentos, logo sem colisão
 * com `/oferta/:cursoSlug`, que tem dois. **E não é `/trilha/:slug`**, que é a
 * página do MEMBRO e chama `exigirMatricula`: era o endereço que o painel
 * anunciava como «está no ar» desde a `0467`, e era falso para toda visita de
 * fora — o interruptor `area_track.pagina_publica` não tinha um único leitor em
 * caminho servido.
 */
const PromoDaTrilha = telaComDicionario(() =>
  import('./rotas/Promo.js').then((m) => ({ default: m.PromoDaTrilha })),
);
/*
 * ── AS TRÊS TELAS QUE SAÍRAM DAQUI EM 25/09/2026, E O QUE ISSO DEVOLVEU ─────
 *
 * `/esqueci-senha`, `/primeiro-acesso` e `/perfil` estavam escritas dentro deste
 * ficheiro, e por isso viviam no **pedaço de entrada** — o que toda pessoa que
 * abre a área descarrega antes de ver a primeira aula. Nenhuma das três está no
 * caminho de nada: duas são a porta de quem ainda não tem senha, e a terceira é a
 * visita mais rara da área.
 *
 * Medido pelo método desta casa, no MESMO build, trocando cada rota por
 * `element={null}` para o minificador as deixar cair:
 *
 * | o que saiu | devolveu |
 * |---|---|
 * | `EsqueciSenha` + `PrimeiroAcesso` → `entrada/PortasDoCodigo.tsx` | 3.087 B |
 * | `Perfil` → `rotas/MinhaConta.tsx` | 2.750 B |
 * | **as três juntas** | **5.837 B** |
 *
 * **E não custam um pixel de experiência**: o `<Routes>` abaixo já está inteiro
 * dentro de um `<Suspense fallback={<Espera />}>`, e treze das dezoito rotas já eram
 * `lazy()`. Estas passam a comportar-se como as outras treze — sem estado de
 * espera novo, sem vão e sem peça que pisca.
 *
 * O número e o porquê estão em `comunidade/o-editor-nao-entra-na-porta.test.ts`,
 * que é onde o orçamento desta porta se lê.
 */
const EsqueciSenha = telaComDicionario(() =>
  import('./entrada/PortasDoCodigo.js').then((m) => ({ default: m.EsqueciSenha })),
);
const PrimeiroAcesso = telaComDicionario(() =>
  import('./entrada/PortasDoCodigo.js').then((m) => ({ default: m.PrimeiroAcesso })),
);
const Perfil = telaComDicionario(() =>
  import('./rotas/MinhaConta.js').then((m) => ({ default: m.Perfil })),
);
const TrajetoDaTrilha = telaComDicionario(() =>
  import('./componentes/TrajetoDaTrilha.js').then((m) => ({ default: m.TrajetoDaTrilha })),
);
const Certificado = telaComDicionario(() =>
  import('./componentes/CertificadoDoCurso.js').then((m) => ({ default: m.Certificado })),
);

/**
 * A frase de espera dos pedaços sob demanda — a mesma que a tela mostraria
 * buscando dados.
 *
 * ## Por que virou COMPONENTE, e já não uma constante
 *
 * Porque ela passou a traduzir-se (`A8`), e o `useT()` é um hook: uma
 * constante JSX avaliada no topo do módulo não tem onde o chamar. A primeira
 * conversão pôs o hook aqui e o React recusou — *«Invalid hook call»* —, e foi
 * uma cerca de integração que o apanhou.
 *
 * Nada mais muda: os cinco `<Suspense>` que a usam — quatro aqui e um em
 * `MinhaConta` — passam de `fallback={ESPERA}` a `fallback={<Espera />}`.
 */
export function Espera() {
  const t = useT();
  return (
    <p className="px-6 pt-10 text-sm" style={{ color: 'var(--texto-suave)' }}>
      {t('estado.carregando')}
    </p>
  );
}

/**
 * A área do aluno.
 *
 * Uma SPA própria, servida em `/aluno/`. O que ela **não** tem é tão deliberado
 * quanto o que tem: nenhuma rota de `/api/*`, nenhuma noção de cliente ou
 * agência, nenhum gráfico. É a tela de quem comprou um curso.
 */

/**
 * A ponte com o Estúdio, carregada **só dentro de um iframe**.
 *
 * `import()` dinâmico e não estático: fora do estúdio o módulo nunca entra no
 * bundle inicial, e o aluno não paga por uma tela que só o dono vê. A condição é
 * `window.parent !== window` porque é isso que o modo é — a página do aluno
 * sendo olhada de dentro de outra.
 */
function useModoEstudio(): void {
  useEffect(() => {
    if (window.parent === window) return;

    let desligar: (() => void) | undefined;
    let vivo = true;

    void import('./lib/modo-estudio.js').then((m) => {
      if (vivo) desligar = m.ligarModoEstudio();
      else m.ligarModoEstudio()();
    });

    return () => {
      vivo = false;
      desligar?.();
    };
  }, []);
}

/**
 * ── O FUNDO DE UMA ROTA-MODAL (ordem do dono, 18/09/2026) ───────────────────
 *
 * *«ao clicar em uma postagem … ela pode abrir em um popup»*, e ao mesmo tempo
 * *«o endereço continua a existir para quem abre o link direto»*.
 *
 * As duas coisas ao mesmo tempo são o padrão de **rota-modal**: quem clica num
 * cartão do mural navega para o endereço do post levando a `location` do mural
 * em `state.fundo`. Vendo o fundo, a tabela de rotas continua a desenhar **o
 * mural** — o mesmo elemento, sem desmontar, com a rolagem onde estava — e o
 * popup entra por cima, numa segunda tabela.
 *
 * **O `state` não sobrevive ao recarregar, e isso é a metade boa:** sem ele o
 * endereço tem de valer sozinho, e vale — cai na `PaginaDoPost`, que desenha a
 * MESMA conversa. *Um popup restaurado por cima de um mural que ninguém pediu
 * custaria três pedidos de rede a quem clicou num link para ler um post.*
 */
function fundoDaRota(estado: unknown): { pathname: string; search: string } | null {
  const f = (estado as { fundo?: { pathname?: unknown; search?: unknown } } | null)?.fundo;
  if (!f || typeof f.pathname !== 'string') return null;
  return { pathname: f.pathname, search: typeof f.search === 'string' ? f.search : '' };
}

export default function App() {
  useModoEstudio();
  const local = useLocation();
  const fundo = fundoDaRota(local.state);

  return (
    /*
     * ── O PROVEDOR ENVOLVE AS TRÊS DE FORA, E NÃO SÓ A TABELA (26/10/2026) ──
     *
     * Ele esteve a envolver só as `<Routes>`, e as três superfícies que vivem
     * FORA delas — a faixa de suporte, o portão dos termos e o convite do
     * Instagram — ficavam sem dicionário: o `useT()` delas caía na base, que é
     * portuguesa. Numa área em espanhol, com o menu todo em `es`, o convite
     * perguntava **«Qual é o seu @ do Instagram?»**.
     *
     * Nenhuma cerca via isto, e nenhuma podia: as três chamam `t()` com chaves
     * que EXISTEM e estão traduzidas nas seis línguas. O que faltava era o
     * contexto, e contexto não se lê num ficheiro — lê-se na árvore montada.
     * Quem o achou foi o navegador, com a área aberta em `es-ES`.
     *
     * Ele pode estar aqui porque só depende de `useLocation()` (está dentro do
     * `Router`) e lê o slug do caminho, nunca de `useParams` — a razão está
     * escrita em `lib/idioma.tsx`. E não custa um byte na entrada: já era
     * importado estaticamente.
     */
    <ProvedorDeIdioma>
      {/*
       * A faixa fica FORA das rotas, e é o motivo de ela estar aqui em cima.
       *
       * Dentro de uma rota ela desmontaria a cada navegação — e a navegação é
       * justamente o que o operador vai fazer o tempo todo (vitrine → curso →
       * aula → prova). Uma faixa que pisca a cada clique é uma faixa que se
       * aprende a ignorar, e o dia em que ela some por um instante é o dia em que
       * alguém fecha a aba achando que era a própria conta.
       *
       * Para quem não está numa sessão de suporte ela devolve `null` na primeira
       * linha: o custo é a leitura de um cookie.
       */}
      {/*
       * ── AS TRÊS DE FORA TAMBÉM TÊM CERCA (23/09/2026) ─────────────────────
       *
       * A cerca lá em baixo diz, por escrito, que existe «para o PRÓXIMO»
       * — *«um componente que quebre perde a sua caixa, e o resto da sala
       * continua de pé»*. Estas três estavam **fora** dela, e são as únicas do
       * app que estão: qualquer erro num render, num efeito ou numa desmontagem
       * delas derruba a RAIZ inteira, e o React 19 esvazia o `#root`. O que se
       * vê então é o fundo do documento, sem cabeçalho, sem lista e sem saída —
       * a mesma tela preta de 04/09/2026, que só um `reload` desfaz.
       *
       * Uma cerca por peça, e não uma à volta das três: com uma só, a queda da
       * faixa de suporte levaria o portão dos termos junto. Cada uma perde a
       * sua caixa, que é exatamente a lei que a cerca escreve.
       *
       * Elas continuam fora das ROTAS — o motivo de cada uma estar aqui em cima
       * não mudou. O que mudou é que já não podem levar a área com elas.
       */}
      <CercaDeErro oQue="cerca.faixaDeSuporte">
        <FaixaDeSuporte />
      </CercaDeErro>
      {/* O portão dos termos, fora das rotas pelo mesmo motivo da faixa: ele é
          da ÁREA, não de uma tela. Ele NÃO é a tranca — a tranca é o 403 do
          servidor em toda rota `/a/:slug/...`. Ver `PortaoDosTermos.tsx`. */}
      <CercaDeErro oQue="cerca.avisoDosTermos">
        <PortaoDosTermos />
      </CercaDeErro>
      {/* O convite do Instagram — fora das rotas pelo mesmo motivo dos dois
          acima, e DEPOIS do portão dos termos de propósito: ele cala-se enquanto
          houver termos por aceitar (lei 3, em `PortaoDoInstagram.tsx`). Ele não
          tranca nada: é um campo opcional, e quem fecha tem o curso inteiro. */}
      {/*
        ── ELE DESCE SOB DEMANDA, E O NÚMERO É QUEM MANDA ────────────────────

        Medido nesta leva: com o portão importado estaticamente, o pedaço de
        entrada foi a **335.802 B** contra o teto de 335.000 — 802 B acima, e a
        `EDT-12` (`comunidade/o-editor-nao-entra-na-porta`) ficou vermelha. Está
        escrito lá que a resposta não é subir o teto: é «medir o que está na porta
        e tirar o que não precisa de estar».

        Este convite é visto **uma vez na vida** de cada aluno. Nem a decisão
        precisa de estar na porta — ela depende de `/eu`, que nenhuma outra tela
        da entrada pede. Então o pedaço inteiro desce pelo fio, com `fallback`
        nulo: nada atrasa, nada pisca, e quem já respondeu nunca mais o vê.
      */}
      <CercaDeErro oQue="cerca.conviteDoInstagram">
        <Suspense fallback={null}>
          <PortaoDoInstagram />
        </Suspense>
      </CercaDeErro>
      {/* A página nova começa no topo. Fora das rotas pelo mesmo motivo dos
          dois acima. Ver `RolarAoTopo.tsx`. */}
      <CercaDeErro oQue="cerca.rolagemDaPagina">
        <RolarAoTopo />
      </CercaDeErro>
      {/* A cerca da ÁREA: nenhuma tela que caia leva o `#root` junto. Ver
          `CercaDeErro.tsx` — a tela preta ao voltar de um PDF. */}
      {/*
       * A DOCA NÃO ESTÁ AQUI, e isso é uma decisão medida.
       *
       * A conversa do assistente precisa de sobreviver ao clique numa citação —
       * que desmonta a sala inteira —, e a resposta óbvia era um provedor nesta
       * altura. Ela custava **3.122 B no pedaço de entrada**, num teto que tinha
       * 190 B de folga (`comunidade/o-editor-nao-entra-na-porta.test.ts`). A
       * loja passou a ser um módulo, alcançado só pelas peças da Doca, no pedaço
       * da sala: sobrevive melhor e a porta do aluno não paga nada. Ver
       * `doca/estado.ts`.
       */}
      <CercaDeErro oQue="cerca.aArea">
        <Suspense fallback={<Espera />}>
          {/* `location={fundo ?? local}`: com fundo, esta tabela continua a casar o
          MURAL, e é isso que o mantém montado por trás do popup. */}
          <Routes location={fundo ?? local}>
            <Route path="/entrar" element={<Entrar />} />
            {/*
          O CADASTRO ESPONTÂNEO (`CE1`) mora em `rotaDaArea('/cadastro')` — o
          endereço que o tutor PUBLICA, e por isso com o slug no caminho e não na
          busca. O porquê está escrito em `rotas/Cadastro.tsx`; aqui fica só o
          motivo de ele estar sob demanda: quem abre `/entrar` não é quem abre o
          cadastro, e V13 orça o pedaço de entrada. A moldura e as peças já estão
          pagas — o pedaço novo é só esta tela.
        */}
            <Route path={rotaDaArea('/cadastro')} element={<Cadastro />} />
            {/*
          A PÁGINA PÚBLICA DE PROMOÇÃO (`0461`), vizinha do cadastro porque é a
          outra rota desta tabela que não pede sessão — e porque é ela que manda
          gente para aquela. Ver o `lazy()` acima para o endereço e o nome.
        */}
            {/*
          A da TRILHA vem ANTES, e a ordem é o que a torna correcta: o roteador
          do React Router escolhe por especificidade e não por ordem de
          declaração, mas um curso cujo slug fosse `trilha` casaria
          `/oferta/trilha` com DOIS segmentos — e a trilha pede três. Declarada
          primeiro, a intenção fica legível para quem ler a tabela.
        */}
            <Route path={rotaDaArea('/oferta/trilha/:trilhaSlug')} element={<PromoDaTrilha />} />
            <Route path={rotaDaArea('/oferta/:cursoSlug')} element={<Promo />} />
            <Route path="/primeiro-acesso" element={<PrimeiroAcesso />} />
            <Route path="/esqueci-senha" element={<EsqueciSenha />} />
            <Route path="/perfil" element={<Perfil />} />
            {/*
          A RAIZ tem dois donos, e quem decide é o HOST (`0276`).
          ─────────────────────────────────────────────────────────────────────
          No host do painel, `/` é o SELETOR: quem estuda em duas áreas de
          clientes diferentes chega aqui e escolhe. No domínio próprio de uma
          área, o host JÁ escolheu — `curso.cliente.com.br` só tem uma área, e
          oferecer uma lista de uma linha seria uma tela a mais entre quem
          comprou e o curso que ele comprou.

          Duas `<Route path="/">` não podiam coexistir: o React Router casa a
          primeira e a vitrine nunca abriria. Por isso o seletor sai da tabela
          quando há área de host, em vez de ficar depois — depois é igual a
          nunca, e um `<Route>` inalcançável é pior que ausente, porque parece
          que funciona.
        */}
            {!AREA_DO_HOST && <Route path="/" element={<Seletor />} />}
            {/* A prévia do Estúdio: sem sessão e sem fetch. Ver `rotas/Previa.tsx`. */}
            <Route path="/previa" element={<Previa />} />
            <Route path={rotaDaArea('')} element={<PaginaDaVitrine />} />
            {/* A LISTA (plural) e UMA trilha (singular). Dois endereços, e nenhum
            deles é a âncora que morreu com a seção — ver `rotas/Trilhas.tsx`. */}
            <Route path={rotaDaArea('/trilhas')} element={<PaginaDasTrilhas />} />
            <Route path={rotaDaArea('/trilha/:trilhaSlug')} element={<PaginaDaTrilha />} />
            {/* A agenda e o mural. As duas moram DENTRO da área — como tudo o mais
            aqui, o que se vê depende da matrícula e dos grupos de acesso. */}
            <Route path={rotaDaArea('/agenda')} element={<PaginaDaAgenda />} />
            {/* As duas listas do aluno. A rota existia completa no servidor e não
            tinha leitor nenhum aqui — os botões da sala de aula gravavam num
            lugar sem tela. Ver `rotas/MinhasAulas.tsx`. */}
            <Route path={rotaDaArea('/minhas-aulas')} element={<PaginaDeMinhasAulas />} />
            <Route path={rotaDaArea('/comunidade')} element={<PaginaDaComunidade />} />
            {/* ANTES de `/:postId`, e o segmento estático ganharia de qualquer
            forma no `react-router` — a ordem aqui é para quem lê. */}
            <Route path={rotaDaArea('/comunidade/escrever')} element={<PaginaDeEscrever />} />
            {/* ANTES de `/:postId` por leitura, e sem ambiguidade real: `:postId` é um
            segmento e este caminho tem dois. Sem o `state.fundo` — recarregar,
            colar, abrir noutra aba — o encontro não tem página própria: a casa
            dele é a Agenda, e é para lá que ele vai. Ver `PopupDoEncontro`. */}
            <Route
              path={rotaDaArea('/comunidade/encontro/:eventoId')}
              element={<EncontroLevaAAgenda />}
            />
            <Route path={rotaDaArea('/comunidade/:postId')} element={<PaginaDoPost />} />
            <Route path={rotaDaArea('/perfil')} element={<PaginaDoPerfil />} />
            <Route path={rotaDaArea('/c/:cursoId')} element={<PaginaDoCurso />} />
            <Route path={rotaDaArea('/aula/:aulaId')} element={<PaginaDaAula />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {/*
        OS POPUPS, por cima do fundo.

        Eram um só, e o comentário que estava aqui dizia que uma segunda entrada
        seria «a tentação de pôr em popup coisas que são páginas». A segunda
        chegou por ordem do dono de 27/09/2026 — o encontro da agenda — e a régua
        não se contorna, muda: **abre em popup o que se lê sem sair de onde se
        estava**. A conversa de um post e os dados de um encontro são as duas
        coisas que se consultam a meio de outra leitura; uma aula, um curso e a
        agenda inteira não são, e continuam páginas.

        A prova de que não é tentação está no fim de cada popup: os dois levam à
        página que os mostra por inteiro.
      */}
          {fundo && (
            <Routes>
              <Route path={rotaDaArea('/comunidade/:postId')} element={<PopupDoPost />} />
              <Route
                path={rotaDaArea('/comunidade/encontro/:eventoId')}
                element={<PopupDoEncontro />}
              />
            </Routes>
          )}
        </Suspense>
      </CercaDeErro>
    </ProvedorDeIdioma>
  );
}

/**
 * O endereço de um encontro, sem o popup por trás, leva à Agenda.
 *
 * O popup do encontro nasce de `state.fundo`, como o do post. A diferença é o
 * que acontece quando o estado não existe — recarregar, colar o endereço, abrir
 * noutra aba: o post tem página própria e vai para ela; **o encontro não tem, e
 * não vai ganhar uma** só para repetir o que a folha do encontro na Agenda já
 * desenha. Um terceiro lugar a mostrar o mesmo encontro é um terceiro lugar para
 * a hora, o link e a descrição divergirem.
 *
 * `replace` porque o endereço não é um lugar onde se esteve: deixá-lo na pilha
 * faria o «voltar» tentar reabrir um popup sem fundo, e cair aqui outra vez.
 */
function EncontroLevaAAgenda() {
  const { areaSlug: slugDaRota } = useParams();
  const areaSlug = useAreaSlug(slugDaRota);
  return <Navigate to={caminhoDaArea(areaSlug, '/agenda')} replace />;
}

/**
 * O convite que substitui o "não encontrado".
 *
 * Quem chegou aqui está logado, já comprou alguma coisa e acabou de demonstrar
 * interesse no que ainda não tem. Um 404 seria a resposta tecnicamente correta e
 * a comercialmente mais cara do produto.
 */
/**
 * ── A TELA PRETA: ESTA PEÇA NÃO TINHA CASCA (ordem do dono, 02/10/2026) ─────
 *
 * > *«ao clicar no curso … só abre uma tela preta. Deveria ter um
 * > redirecionamento para o link cadastrado e na ausência do link uma tela
 * > dizendo que ele não tem acesso ao curso.»*
 *
 * **A tela preta era esta, e a causa é de uma linha.** Ela devolvia um `<div>`
 * nu. Nenhum ancestral desta rota põe tema: as rotas do app são planas
 * (`<Route path="/c/:cursoId">` directamente sob `<Routes>`), e nesta casa o
 * tema é aplicado **no elemento raiz de cada tela** — `index.css` diz-no em
 * letra grossa, porque a pessoa troca de área sem recarregar e um tema em
 * `:root` deixaria a cor de um cliente por baixo da área de outro.
 *
 * O preço, medido na leitura: `html`/`body` recebem o fundo semeado da área
 * (`#0b0b0f` de reserva) e **ninguém declarava `color`** — o texto herdava o
 * preto de fábrica do navegador. `--destaque` e `--sobre-destaque` não
 * existiam neste elemento, e o botão ficava com fundo `transparent` e tinta
 * herdada. Três parágrafos e um botão, pretos, sobre preto: a página respondia
 * 200, tinha o conteúdo certo no DOM, e não se via nada.
 *
 * O conserto é `<Moldura>`, que é a peça que as outras sete telas deste arquivo
 * já usam para exactamente isto — `variaveisDoTema(temaSemeado(slug))`, que
 * resolve de forma síncrona a partir do documento e por isso não pisca. Não é
 * peça nova: é a que já existia, e que esta tela nunca vestiu.
 *
 * ## As duas formas da recusa, numa peça só
 *
 * | o 403 traz | a tela |
 * |---|---|
 * | `oferta` | a frase com o nome do curso, e o botão que leva à página de venda |
 * | `oferta: null` | *«Você não tem acesso a este curso»*, e a porta de volta à vitrine |
 *
 * Duas peças diriam a mesma coisa de duas maneiras no primeiro conserto. E a
 * segunda forma é a metade da ordem que não existia: sem endereço declarado, o
 * servidor respondia **404 «Curso não encontrado»** — uma frase falsa a quem
 * tem matrícula na área, sobre um curso que o tutor publicou. Ver `exigirCurso`.
 *
 * ## Por que a porta de volta é a VITRINE, e não «Entrar»
 *
 * Porque quem chega aqui está dentro: o 403 passou por `exigirMatricula`. A
 * porta de `Recusa` — *Entrar* — é a certa para o 404 da área que não é sua, e
 * a errada para o curso que não é seu dentro da área que é. Um beco não se
 * conserta com a porta de outro beco.
 */
function SemAcessoAoCurso({
  url,
  titulo,
  areaSlug,
}: {
  url: string | null;
  titulo: string | null;
  areaSlug: string;
}) {
  const t = useT();
  return (
    <Moldura>
      <div className="text-center">
        <p className="text-lg font-medium">
          {/* Com nome, o nome; sem nome, o sujeito genérico — e nunca «este
              curso não está no seu acesso» com um nome inventado no meio. O
              servidor só manda o título quando o tutor declarou a página de
              venda; ver a razão em `exigirCurso`. */}
          {titulo === null ? t('estado.semAcessoAoCurso') : `${titulo} não está no seu acesso.`}
        </p>
        {url !== null ? (
          <>
            <p className="mt-2 text-sm" style={{ color: 'var(--texto-suave)' }}>
              {t('acesso.naoTemAinda')}
            </p>
            <a
              href={url}
              /* A mesma aba, aqui: quem está nesta tela já saiu da prateleira —
                 trocar a página por outra é o gesto que ele veio fazer. Na
                 vitrine é o contrário, e o cartaz abre em aba nova. */
              data-forma="saida-sem-acesso"
              className="mt-6 inline-block px-6 py-3 font-medium"
              style={{
                background: 'var(--destaque)',
                color: 'var(--sobre-destaque)',
                borderRadius: 'var(--raio)',
              }}
            >
              {t('nav.verO', { titulo: titulo ?? t('nav.oCurso') })}
            </a>
          </>
        ) : (
          <>
            <p className="mt-2 text-sm" style={{ color: 'var(--texto-suave)' }}>
              {t('acesso.deOutroAcesso')}
            </p>
            <Link
              to={caminhoDaArea(areaSlug)}
              data-forma="saida-sem-acesso"
              className="mt-6 inline-block px-6 py-3 font-medium"
              style={{
                background: 'var(--destaque)',
                color: 'var(--sobre-destaque)',
                borderRadius: 'var(--raio)',
              }}
            >
              {t('nav.voltarParaCursos')}
            </Link>
          </>
        )}
      </div>
    </Moldura>
  );
}

// ------------------------------------------------------------------ entrada

/**
 * O quadro de espera e de recusa das telas que carregam por `useCarregar`.
 *
 * ## Por que ele lê o slug da ROTA, e não o recebe por prop (`CE3`)
 *
 * Porque `variaveisDoTema()` sem argumento é o tema de FÁBRICA — `#0b0b0f` de
 * fundo, `#22c55e` de destaque —, e é isso que se via enquanto a vitrine não
 * chegava: o «Carregando…» no preto da casa, e depois a área inteira a trocar de
 * cor. O mesmo piscar de `entrada/Moldura.tsx`, no outro lado do produto.
 *
 * `temaSemeado` responde de forma síncrona a partir do documento
 * (`lib/semente.ts`), e o slug para lho pedir está na rota — `useParams`, que é
 * de onde as sete telas que usam este quadro já o tiram. Uma prop teria de ser
 * passada em sete chamadas, e a oitava seria a que alguém esqueceria; e lê-lo
 * aqui não custa pedido nenhum.
 *
 * Sem semente — desenvolvimento, seletor de áreas, um slug que não existe —
 * `temaSemeado` devolve `undefined` e o quadro é o de fábrica, exactamente como
 * antes.
 */
export function Moldura({ children }: { children: React.ReactNode }) {
  const { areaSlug } = useParams();
  const slug = useAreaSlug(areaSlug);
  return (
    <div className="min-h-screen" style={variaveisDoTema(temaSemeado(slug))}>
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6">
        {children}
      </div>
    </div>
  );
}

/**
 * ── A RECUSA TEM PORTA (`B2`) ───────────────────────────────────────────────
 *
 * As cinco telas que carregam por `useCarregar` desenhavam
 * `<Moldura>{erro}</Moldura>`: a frase crua do servidor, centrada no preto, com
 * **zero** elementos clicáveis. Um beco sem saída — a pessoa fecha a aba, e o
 * dono do curso nunca sabe que ela chegou e voltou.
 *
 * **Medido em 06/09/2026 (§64 de `docs/forma-arquitetura.md`).** Quem tem sessão
 * de aluno de OUTRA área e abre `/a/<slug>` recebe **404** de `exigirMatricula`
 * — que não é `precisaEntrar` (que é o 401), não é `curso_nao_comprado` e não é
 * `aula_bloqueada`: é o quarto ramo, o que `useCarregar` entrega em `erro`. O
 * 401 já tinha caminho bom (`replace` para a tela de entrar, com a marca da
 * área); o 404 não tinha nenhum.
 *
 * ## O que esta peça faz, e o que ela recusa fazer
 *
 * **Um gesto, e é o mesmo gesto do 401.** `enderecoDeEntrar` é a função que a
 * ida do 401 já usa (`carregar.ts`), com o mesmo `?a=<slug>`: as duas recusas
 * passam a terminar na mesma porta, escrita uma vez. Não é peça nova nem token
 * novo — o desenho é o do `ConviteDaOferta` logo acima, que é o precedente desta
 * casa para «recusa que desenha porta em vez de parede», com os mesmos
 * `--destaque` / `--sobre-destaque` / `--raio`.
 *
 * **Ela NÃO inventa a marca da área**, e a ausência é honesta: a marca vem da
 * área, e o 404 é justamente a resposta que diz que não se alcançou a área. Uma
 * casca com a marca genérica a fingir-se da área seria pior que nenhuma.
 *
 * **Ela NÃO inventa a causa.** A frase é a do servidor, palavra por palavra: a
 * §63.4 recusou acrescentar legenda a um beco não reproduzido, e a regra
 * continua a valer para o beco reproduzido — o que faltava era a saída, não a
 * explicação.
 *
 * **`areaSlug` opcional, e sem `?? ''`.** `Perfil` e `Seletor` carregam `/eu` e
 * não têm área nenhuma; `enderecoDeEntrar(undefined)` devolve `/entrar` liso, e
 * o gesto continua a existir. Um `?? ''` daria `?a=` vazio — o `??` que
 * transforma o defeito em silêncio, que é o mesmo pé em que o `B3` tropeçou.
 */
export function Recusa({ frase, areaSlug }: { frase: string; areaSlug?: string }) {
  const t = useT();
  /**
   * ── A PORTA DO 404 CARREGA O DESTINO, COMO A DO 401 SEMPRE CARREGOU ────────
   *
   * O `B2` prometeu, por escrito, que «as duas recusas passam a terminar na
   * mesma porta, escrita uma vez». Metade era verdade: as duas chamam
   * `enderecoDeEntrar`, mas o 401 passa-lhe o caminho pedido
   * (`carregar.ts`, `${local.pathname}${local.search}`) e esta passava-lhe
   * **nada**. As portas eram duas — `/entrar?a=<slug>` e
   * `/entrar?a=<slug>&destino=…` —, e o preço não é um erro: quem chega aqui
   * pelo link de um curso, e entra com a conta certa, aterra no SELETOR DE
   * ÁREAS em vez do curso que pediu. É o mesmo defeito que `destino.ts` nasceu
   * a corrigir, sobrevivendo no ramo que ninguém tinha ligado ao destino.
   *
   * `useLocation` e não `window.location`, pelo motivo que `useCarregar` já
   * escreve: o roteador é montado com `basename="/aluno"`, e o caminho do
   * navegador traz um prefixo que a navegação interna não entende.
   *
   * Isto **não** afrouxa a indistinção de `exigirMatricula`: o destino é o
   * caminho que a própria pessoa acabou de pedir, e não uma informação nova
   * sobre a área. A frase continua a ser a do servidor, palavra por palavra, e
   * continua a ser a MESMA para «não existe» e para «não é sua» — esta peça não
   * consegue distingui-las, e é por isso que o que ela escreve também não pode.
   */
  const local = useLocation();
  return (
    <Moldura>
      <div className="text-center">
        <p className="text-lg font-medium">{frase}</p>
        <Link
          to={enderecoDeEntrar(areaSlug, `${local.pathname}${local.search}`)}
          data-forma="saida-da-recusa"
          className="mt-6 inline-block px-6 py-3 font-medium"
          style={{
            background: 'var(--destaque)',
            color: 'var(--sobre-destaque)',
            borderRadius: 'var(--raio)',
          }}
        >
          {t('acesso.entrar')}
        </Link>
      </div>
    </Moldura>
  );
}

/* `MolduraDaEntrada` mora em `entrada/Moldura.tsx` desde a leva L1: a tela de entrar
   ganhou seis estruturas, e as quatro novas vêm por um pedaço à parte. */

/**
 * A tela de entrar.
 *
 * ## A hierarquia, e por que ela mudou
 *
 * O campo de senha era o destaque e o código era um link cinza embaixo. Numa
 * área de membros isso está invertido: **a maioria das contas nasce sem senha**
 * — toda concessão (compra, importação em lote, voucher, matrícula manual) cria
 * a conta e um `member_access_code`, e a senha só passa a existir depois que o
 * aluno a define. Quem chega aqui pela primeira vez, que é a maioria de quem
 * chega, precisa do código; a tela oferecia a senha.
 *
 * O desenho não foi refeito: o formulário de senha continua sendo o primeiro,
 * porque quem já entrou uma vez volta muitas. O que mudou é o **peso** do
 * segundo caminho — de link cinza dentro de uma pilha de dois links iguais para
 * um bloco próprio, com moldura, título e a frase que diz para quem ele é. Ele
 * continua visível para **todo mundo**, sempre, e essa parte não pode mudar:
 * mostrá-lo só a quem "ainda não tem senha" exigiria a tela saber quem tem
 * conta, e é exatamente o vazamento que o servidor recusa (ver
 * `entrada-do-aluno.ts`).
 *
 * ## O que a recusa faz com ele
 *
 * O servidor devolve `detalhes.caminho = 'codigo'` em **toda** recusa — o mesmo
 * valor nas três, então ele não conta nada sobre a conta —, e a tela usa isso
 * para acender o bloco. Acender, e não revelar: o bloco já estava lá antes do
 * erro.
 */
/**
 * O id da recusa de entrar, fixo e num lugar só.
 *
 * Fixo, e não `useId()`: há UMA tela de entrar viva de cada vez, e os dois campos
 * que apontam para a frase precisam do mesmo valor. Um `useId` por campo daria
 * dois ids diferentes para a mesma frase — que é o defeito, não o conserto.
 */
const ID_DA_RECUSA_DE_ENTRAR = 'vst-recusa-de-entrar';

function Entrar() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [realcarCodigo, setRealcarCodigo] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const navegar = useNavigate();

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    try {
      await api.post('/entrar', { email, senha });
      /* De volta ao que a pessoa pediu, e não ao seletor de áreas: quem clicou
         no link de um curso veio para assistir àquele curso. Ver `destino.ts`. */
      navegar(destinoGuardado(window.location.search), { replace: true });
    } catch (err) {
      setErro(err instanceof Error ? err.message : t('estado.naoDeuParaEntrar'));
      /* `caminho` é constante nas três recusas do servidor — o realce diz "há
         outro caminho", nunca "a sua conta é assim". */
      setRealcarCodigo(err instanceof ErroDaApi && err.detalhes?.caminho === 'codigo');
    } finally {
      setEnviando(false);
    }
  }

  /* `pecas` como FUNÇÃO da cara (`CE3`): a porta do cadastro só se desenha se a
     área a tiver aberto, e é a cara que o diz. Ver `entrada/Moldura.tsx`. */
  return (
    <MolduraDaEntrada
      pecas={(cara) => ({
        titulo: 'Entrar',
        formulario: (
          <form onSubmit={enviar} className="mt-6 flex flex-col gap-3">
            <Campo
              rotulo={t('conta.email')}
              tipo="email"
              valor={email}
              preencher="email"
              descritoPor={erro ? ID_DA_RECUSA_DE_ENTRAR : undefined}
              aoMudar={setEmail}
            />
            <Campo
              rotulo={t('conta.senha')}
              tipo="password"
              valor={senha}
              preencher="current-password"
              descritoPor={erro ? ID_DA_RECUSA_DE_ENTRAR : undefined}
              aoMudar={setSenha}
            />
            {/* Colada ao campo Senha, e não no rodapé da tela. O porquê está em
                `entrada/PecasDoEntrar.tsx`. */}
            <EsqueciASenha busca={window.location.search} />
            {erro && (
              <p
                id={ID_DA_RECUSA_DE_ENTRAR}
                className="text-sm leading-relaxed text-red-400"
                role="alert"
              >
                {erro}
              </p>
            )}
            {/*
              ── O CAMINHO FICA JUNTO DO ERRO, E NÃO A 400px DELE (25/09/2026) ──

              A recusa já acendia o bloco «Recebeu um código de acesso?» — mas o
              que ela acendia era uma **borda de 1px**, num cartão abaixo do
              formulário. Medido em produção: 7 dos 8 alunos com código não
              tinham senha, e o log mostra-os a insistir no login
              (`"motivo":"sem_senha"`). Eles liam «não foi possível entrar» e
              tentavam de novo — a borda não os levou a lado nenhum.

              Agora o caminho é um gesto, no sítio onde o olho acabou de pousar, e
              leva o e-mail já digitado — quem foi recusado não redigita nada.

              Continua sem revelar conta nenhuma: o servidor manda o MESMO
              `caminho: 'codigo'` nas três recusas, então isto diz «há outra
              porta», nunca «a sua conta é assim».
            */}
            {realcarCodigo && (
              <Link
                to={`/primeiro-acesso?${new URLSearchParams({
                  ...Object.fromEntries(new URLSearchParams(window.location.search)),
                  ...(email ? { email } : {}),
                }).toString()}`}
                className="text-sm font-medium underline"
                style={{ color: 'var(--destaque)' }}
              >
                {t('acesso.entrarComCodigoDeAcesso')}
              </Link>
            )}
            <Botao carregando={enviando}>{t('acesso.entrar')}</Botao>
          </form>
        ),
        /* O caminho do código fica visível para todo mundo de propósito: oferecê-lo
           só a quem "ainda não tem senha" exigiria dizer quem tem conta. As duas peças
           moram em `entrada/PecasDoEntrar.tsx` porque a prévia da bancada as desenha. */
        secundario: (
          <SecundarioDoEntrar
            realcar={realcarCodigo}
            busca={window.location.search}
            cadastroAberto={cara?.cadastroAberto ?? false}
            slug={slugDaEntrada()}
          />
        ),
      })}
    />
  );
}

export function Campo({
  rotulo,
  tipo,
  valor,
  desligado = false,
  preencher,
  ajuda,
  erro,
  descritoPor,
  aoMudar,
}: {
  rotulo: string;
  tipo: string;
  valor: string;
  /** O campo existe e não aceita digitação agora. O motivo é dito uma vez, fora. */
  desligado?: boolean;
  /**
   * O `autocomplete` do HTML (`email`, `name`, `current-password`,
   * `new-password`…). Ausente até `CE1`, e a ausência custava caro exactamente
   * onde mais dói: **num formulário de cadastro**, o gestor de senhas não
   * distingue «a senha que você tem» de «a senha que você está a criar» sem esta
   * palavra, e oferece a errada — ou não oferece nenhuma. Opcional de propósito:
   * os campos que já existiam não mudam de comportamento por causa disto.
   */
  preencher?: string;
  /**
   * A regra dita ANTES de a pessoa falhar.
   *
   * Nasceu de um aluno trancado fora da área em produção (25/09/2026): o campo
   * de senha exigia dez caracteres e não o dizia em lado nenhum, e a recusa que
   * ele lia era «Dados inválidos.» — uma frase que aponta para o formulário
   * inteiro, num campo cujo conteúdo a tela esconde. Uma regra que só existe na
   * recusa é uma regra que a pessoa descobre por tentativa.
   *
   * Opcional, e por isso nenhum campo que já existia muda de aspecto.
   */
  ajuda?: string;
  /**
   * ── A RECUSA NO CAMPO QUE A CAUSOU (01/10/2026) ──────────────────────────
   *
   * Até aqui as três telas da porta — entrar, cadastro e primeiro acesso —
   * punham toda recusa num parágrafo no RODAPÉ do formulário. Para quem lê com
   * os olhos isso já é uma caça; para quem usa leitor de tela é pior: o campo
   * não se declara inválido, e voltar a ele com o teclado não repete a frase.
   * Num formulário com dois campos de senha cujo conteúdo a tela esconde, a
   * recusa sem campo manda conferir os dois.
   *
   * Com `erro`, o campo passa a ter `aria-invalid` e `aria-describedby` a
   * apontar para a frase — que é a mesma ligação que a conta já fazia desde
   * que foi redesenhada. Opcional: nenhum campo que já existia muda de aspecto.
   */
  erro?: string | null;
  /**
   * O id da recusa que mora FORA deste campo, quando ela é do formulário.
   *
   * «E-mail ou senha inválidos» é a recusa do PAR — o servidor recusa-se a dizer
   * qual dos dois, e com razão: dizê-lo conta quem tem conta nesta área. Então os
   * dois campos ficam marcados como inválidos e os dois apontam para a MESMA
   * frase, que é desenhada uma vez só. Passar `erro` aos dois desenharia a frase
   * duas vezes; passá-la a um só mentiria sobre qual deles o servidor acusou.
   */
  descritoPor?: string;
  aoMudar: (v: string) => void;
}) {
  /*
   * `useId` e um `<label htmlFor>` em vez do `<label>` a envolver o `<input>`.
   *
   * O embrulho ligava o rótulo sozinho, e era suficiente enquanto não havia
   * nada mais a ligar. A nota — ajuda ou recusa — precisa de id próprio para o
   * `aria-describedby` a alcançar, e um id precisa de ser único na página: esta
   * porta desenha o mesmo `Campo` quatro vezes no primeiro acesso.
   */
  const base = useId();
  const idCampo = `${base}-c`;
  const idNota = `${base}-n`;
  const temNota = Boolean(erro || ajuda);
  const descricao = [temNota ? idNota : null, descritoPor ?? null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1 text-sm">
      <label htmlFor={idCampo} style={{ color: 'var(--texto-suave)' }}>
        {rotulo}
      </label>
      <input
        id={idCampo}
        type={tipo}
        value={valor}
        onChange={(e) => aoMudar(e.target.value)}
        disabled={desligado}
        aria-disabled={desligado || undefined}
        aria-invalid={erro || descritoPor ? true : undefined}
        aria-describedby={descricao || undefined}
        autoComplete={preencher}
        /* `vst-campo` e não `outline-none` (`U5`): a classe põe o contorno em
           repouso e o anel de foco na cor da ÁREA. Os campos da entrada eram o
           último bolso desta casa com `outline-none` e nada no lugar — um campo
           sem borda sobre o fundo da própria página, mudo depois do clique. */
        className="px-3 py-2 vst-campo disabled:opacity-60"
        style={{
          background: 'var(--superficie)',
          color: 'var(--texto)',
          borderRadius: 'var(--raio)',
        }}
      />
      {erro ? (
        /*
         * `role="alert"` para a frase ser ANUNCIADA quando aparece, e não só
         * descrita quando o foco volta ao campo. As duas coisas são precisas, e
         * são diferentes: o `aria-describedby` serve quem volta, o `alert` serve
         * quem acabou de carregar no botão.
         */
        <span id={idNota} role="alert" className="text-xs leading-relaxed text-red-400">
          {erro}
        </span>
      ) : ajuda ? (
        <span id={idNota} className="text-xs" style={{ color: 'var(--texto-suave)' }}>
          {ajuda}
        </span>
      ) : null}
    </div>
  );
}

export function Botao({
  children,
  carregando,
  desligado = false,
}: {
  children: React.ReactNode;
  carregando?: boolean;
  desligado?: boolean;
}) {
  const t = useT();
  return (
    <button
      type="submit"
      disabled={carregando || desligado}
      aria-disabled={desligado || undefined}
      className="mt-2 py-2.5 font-medium disabled:opacity-60"
      style={{
        background: 'var(--destaque)',
        color: 'var(--sobre-destaque)',
        borderRadius: 'var(--raio)',
      }}
    >
      {carregando ? t('assistente.umInstante') : children}
    </button>
  );
}

// ------------------------------------------------------------------ seletor

/**
 * O seletor de áreas.
 *
 * **A única tela do sistema que junta clientes diferentes numa lista só** — e é
 * segura exatamente aqui: quem olha é a pessoa dona dos dois cadastros. Note o
 * que ela não mostra: nome de cliente, nome de agência, nada do outro lado.
 */
function Seletor() {
  const t = useT();
  const { dados, erro } = useCarregar<Eu>(() => api.get('/eu'), []);

  /* Sem área: o seletor é a lista das áreas da pessoa, e a recusa dele é de
     sessão, não de área. */
  if (erro) return <Recusa frase={erro} />;
  if (!dados) return <Moldura>{t('estado.carregando')}</Moldura>;

  /* Uma área só: entrar direto. Uma tela para escolher entre uma opção é um
     clique que não decide nada. */
  if (dados.areas.length === 1)
    return <Navigate to={caminhoDaArea(dados.areas[0]!.slug)} replace />;

  return (
    <div className="min-h-screen" style={variaveisDoTema()}>
      <div className="mx-auto max-w-3xl px-6 py-16">
        <p style={{ color: 'var(--texto-suave)' }}>{t('nav.ola', { nome: dados.membro.nome })}</p>
        <h1 className="mt-1 text-2xl font-semibold">{t('nav.ondeQuerEntrar')}</h1>

        {dados.areas.length === 0 && (
          <p className="mt-8" style={{ color: 'var(--texto-suave)' }}>
            {t('acesso.semAreaAindaAguarde')}
          </p>
        )}

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {dados.areas.map((a) => (
            <Link
              key={a.slug}
              to={caminhoDaArea(a.slug)}
              className="p-5 transition-transform hover:scale-[1.02]"
              /* O tema de cada área aparece já no cartão dela — é o que faz a
                 troca de contexto ser percebida antes do clique. */
              style={{ ...variaveisDoTema(a.tema), borderRadius: 'var(--raio)' }}
            >
              <span className="font-medium">{a.nome}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ vitrine

/*
 * ── `ANCORAS_DA_VITRINE` SAIU, E COM ELA O ROLADOR DE DUAS VOLTAS ───────────
 *
 * Era `['trilhas']`, e existia para que `<vitrine>#trilhas` aterrasse na seção
 * das trilhas numa SPA, onde o navegador não rola porque a seção ainda não
 * existe no instante em que a URL muda. **A seção deixou de existir** — ordem do
 * dono de 06/10/2026, cumprida em `secoesDaVitrine` (`0467`) —, e um rolador para
 * um id que nenhuma página emite é uma porta para o nada que fica no ficheiro a
 * parecer um recurso.
 *
 * A lista tinha um item só e a razão escrita para ela existir — *«não é a
 * conveniência de enumerar, é a recusa de rolar até QUALQUER id que alguém ponha
 * na URL»* — continua de pé e continua escrita onde ainda há âncoras:
 * `ANCORAS_DA_LISTA`, na página do curso. Quem trouxer uma âncora nova à vitrine
 * traz a lista de volta com ela; o que não se deixa é a maquinaria sem alvo.
 *
 * O destino da porta «Trilhas» é agora `/a/:slug/trilhas`, uma rota.
 */

function PaginaDaVitrine() {
  const t = useT();
  const { areaSlug: slugDaRota } = useParams();
  const areaSlug = useAreaSlug(slugDaRota);
  const { dados, erro } = useCarregar<DadosDaVitrine>(() => api.get(`/a/${areaSlug}`), [areaSlug]);
  /* A vitrine é porta de nascimento da gravação quando o escopo alcança a área. */
  useMedicaoDaPagina(dados?.medicao);
  const [cursos, setCursos] = useState<Map<string, CursoDaVitrine>>(new Map());

  /**
   * ── O PEDIDO FICA, E O QUE ELE DESENHAVA SAIU (28/09/2026) ────────────────
   *
   * A tira que lia este progresso saiu da porta de entrada por ordem do dono, e
   * **o pedido continua** — com a resposta deitada fora. Parece código morto e
   * não é: `GET /a/:slug/meu-progresso` **escreve**. Ele calcula os selos
   * merecidos e faz `insert into member_badge … onConflictDoNothing()`, e é esta
   * leitura, na tela que todo aluno abre, que os concede.
   *
   * **A dívida está aqui, escrita, e não é desta leva:** a concessão de um selo
   * está pendurada na visita a uma tela. Quem tirar esta linha sem a mover faz
   * os selos passarem a nascer só quando alguém abrir «Minha conta» — uma tela
   * rara —, e ninguém vai ligar as duas coisas. O sítio certo da concessão é
   * onde o mérito acontece (a aula que fecha, o curso que termina), ao lado de
   * `emitirPremiosCreditados`. *Mudar isso é uma obra própria, com cerca
   * própria, e não um efeito colateral de um pedido de desenho.*
   *
   * Sem estado: ninguém desenha a resposta, e guardá-la num `useState` causaria
   * um render por nada em toda visita à vitrine.
   */
  useEffect(() => {
    void api.get<MeuProgresso>(`/a/${areaSlug}/meu-progresso`).catch(() => undefined);
  }, [areaSlug]);

  useMarca(dados?.marca, dados?.area.nome);

  /*
   * ═══ CHEGAR À VITRINE COM `#trilhas` E ATERRAR NAS TRILHAS ══════════════
   *
   * **Ordem do dono, 01/10/2026:** *«o link de trilhas da barra de menu de
   * agenda deve apontar para a trilha na vitrine, ele tá link morto hoje»*.
   *
   * A metade de lá — em `PortasDaArea` — faz o item apontar para
   * `<vitrine>#trilhas` quando se está noutra página. Esta é a metade de cá, e
   * sem ela o conserto ficava pela metade: *o navegador rola até uma âncora
   * quando a PÁGINA carrega com ela na URL, e numa SPA a navegação é do
   * roteador* — a URL muda, a seção ainda não existe, e a pessoa aterra no
   * topo de uma vitrine inteira a procurar sozinha o que o botão prometeu.
   *
   * É o mesmo rolador da página do curso, com as mesmas duas razões medidas:
   * espera os DADOS (é quando a seção passa a existir) e rola dois quadros
   * depois (um só ainda cai antes de as prateleiras ocuparem a altura delas, e
   * o navegador limita a rolagem ao documento que existe naquele instante).
   *
   * Âncora fechada numa lista, e não `hash` livre: um `scrollIntoView` sobre o
   * que vier na URL é uma porta para qualquer id da página, e a vitrine tem
   * muitos.
   *
   * ── E ESTE ROLADOR FOI APAGADO EM 06/10/2026, COM O ALVO DELE ─────────────
   *
   * A seção `trilhas` saiu de `secoesDaVitrine` por ordem do dono, e `#trilhas`
   * deixou de existir em página nenhuma. O que ficava aqui procurava, dois
   * quadros depois dos dados, um `getElementById('trilhas')` que **nunca mais
   * devolve nada** — e um rolador que não rola é pior que ausente: ele parece
   * explicar como a porta funciona, e a porta já funciona de outra maneira.
   *
   * O texto acima fica porque a lição não é sobre trilhas: *numa SPA, o
   * navegador não honra a âncora da URL, porque o alvo ainda não existe quando a
   * URL muda.* A próxima âncora da vitrine precisa dela, e `ANCORAS_DA_LISTA`
   * (na página do curso) é a cópia viva da mesma regra.
   */

  useEffect(() => {
    if (!dados) return;
    /*
     * As prateleiras trazem ids, e `dados.cursos` traz os objetos. O mapa é o
     * que liga os dois — e o motivo de o servidor mandar assim está lá: o mesmo
     * curso está em duas prateleiras, e repetir o objeto mandaria a capa
     * assinada duas vezes pela rede.
     *
     * O que estava escrito aqui antes era `m.set(id, { id } as CursoDaVitrine)`:
     * um molde vazio, aceito pelo compilador por causa do `as`, que fazia todo
     * cartão da vitrine desenhar título indefinido sobre capa indefinida. Se
     * algum dia `dados.cursos` sumir do contrato, o certo é a tela ficar sem
     * cartão nenhum — e não voltar a ter cartões vazios.
     */
    setCursos(new Map(dados.cursos.map((c) => [c.id, c])));
  }, [dados]);

  if (erro) return <Recusa frase={erro} areaSlug={areaSlug} />;
  if (!dados) return <Moldura>{t('estado.carregando')}</Moldura>;

  return (
    <div className="min-h-screen" style={variaveisDoTema(dados.tema)}>
      <Cabecalho
        titulo={dados.area.nome}
        area={areaSlug}
        /* `marcaDaArea` é o único desembrulhador: o que a rota manda é um objeto
           (`{url, srcset, …}`), e passá-lo direto para o `src` foi o que pôs
           `<img src="[object Object]">` no ar. Agora o tipo recusa o atalho. */
        logo={marcaDaArea(dados.marca).logo}
        menu={dados.menu}
        /*
         * A porta do tutor, na tela que ela foi pedida para existir.
         *
         * A vitrine passa-o porque já o tem na resposta dela; as outras dez
         * rotas não passam nada e o cabeçalho pergunta por si, pelo mesmo
         * pedido dos links do topo. Aqui isto poupa uma ida à rede na tela que
         * mais pesa — a mesma razão de `menu` ser prop e não busca.
         */
        assistente={dados.assistente}
        cabecalho={dados.cabecalho ?? null}
        /* Agenda, Comunidade e Trilhas na MESMA linha do nome da área. Elas
           ocupavam uma faixa inteira abaixo daqui, e essa faixa empurrava o
           herói e tudo o mais para baixo sem dizer nada de novo. */
        portas={
          <PortasDaArea
            areaSlug={dados.area.slug}
            temTrilhas={dados.trilhas.length > 0}
            /* `PER-03`: o sinal chega na MESMA ida que a vitrine, com a frase que
               diz o que conta (`J1299` c6). Ausente num corpo antigo (e na
               prévia do Estúdio) = nada desenhado. */
            cracha={dados.comunidade?.cracha ?? null}
            /*
             * A PORTA (0432). `?? undefined` e nunca `?? false`: um corpo de
             * resposta antigo, a prévia do Estúdio e uma leitura que falhou dizem
             * «não sei», e «não sei» não pode esconder o único item desta barra
             * por onde toda a gente passa. Só o `false` explícito fecha — a lição
             * do 403 de termos que apagou a aba no telemóvel.
             */
            comunidadeAberta={dados.comunidade?.aberta ?? undefined}
          />
        }
        /*
         * ── A FAIXA SUBIU PARA CÁ, E VIROU PASTILHA (25/09/2026) ──────────────
         *
         * Estava aqui, como irmão do `<Cabecalho>`: `<FaixaAoVivo>`, uma faixa de
         * largura inteira que cobrava 76 px em 1440 e 189 px em 390 à primeira
         * tela da área, em toda visita. Ordem do dono: *«essa notificação toma
         * muito espaço, coloque ao lado esquerdo de procurar»*.
         *
         * **Continua a montar aqui, e só aqui.** O `Cabecalho` monta em onze
         * rotas; se a pastilha vivesse dentro dele, as onze passavam a pedir a
         * agenda. É a mesma razão por que `portas` também vem daqui.
         */
        encontro={<PastilhaDoEncontro areaSlug={dados.area.slug} />}
      />
      {/*
       * ── A TIRA DO ESTUDO SAIU DA PORTA DE ENTRADA (28/09/2026, 4.ª ordem) ─
       *
       * Aqui vinha `<TiraDoEstudo>`: «N pontos · N dias seguidos · N cursos
       * concluídos», mais os selos, sobre um fio. **Ordem do dono, com o print
       * da linha:** *«retire essa linha, suba mais os banners, coloque essa
       * informação no perfil do usuário, na conta»*.
       *
       * É a quarta ordem do mesmo dia sobre esta faixa, e as quatro apontam na
       * mesma direção: o quadro «Você por aqui» saiu do fim da vitrine, subiu
       * para cá, perdeu a comparação com a turma, perdeu a frase do placar,
       * perdeu o acervo — e agora sai inteira. *A leitura certa não é «o dono
       * mudou de ideias quatro vezes»: é que **a porta de entrada não é sítio
       * para conferência**, e cada corte aproximou-a disso.*
       *
       * O que a pessoa quer na porta é o que o tutor montou para ser visto
       * primeiro. Tudo o que esta tira dizia é agora lido em Minha conta, ao
       * lado do que ela comprou — ver `MeusAcessos`, onde o acervo já tinha
       * chegado na 3.ª ordem.
       */}
      {/* As trilhas moram DENTRO da vitrine desde a leva A2: são uma seção
          como as outras, que o dono dispõe. */}
      <Vitrine dados={dados} cursos={cursos} />
    </div>
  );
}

/*
 * ── O GLIFO SAI DO `slug`, E O MAPA MORA COM `SELOS` ────────────────────────
 *
 * O que estava aqui era `EMOJI_DO_SELO = ['🎬','🏁','🔥','✅','📚']` — **cinco**
 * figuras — indexado por `i % 5`, onde `i` era a posição na lista devolvida pelo
 * servidor. `SELOS` tem **sete** entradas: o mesmo mérito mudava de figura
 * conforme a ordem em que veio, e os dois selos da comunidade
 * (`primeira_pergunta`, `dez_respostas_uteis`) recebiam 🎬 e 🏁 — «Começou» e
 * «Terminou». O troféu da comunidade aparecia com a cara de outra coisa.
 *
 * O mapa foi para `packages/core/src/area/gamificacao.ts`, ao lado de `SELOS`,
 * porque a distância entre as duas listas era onde o defeito morava. Pelo
 * SUBCAMINHO (`@vestigo/core/area-gamificacao`) e nunca pela raiz: a raiz
 * arrastaria o barril inteiro para o pedaço de entrada do aluno, e `V13` é o
 * único limite desta obra que não é opinião.
 */

/*
 * ── «VOCÊ POR AQUI» SUBIU INTEIRO, E O RODAPÉ FICOU SEM ELE (28/09/2026) ────
 *
 * `BlocoDoProgresso` vivia aqui e desenhava, no fim da vitrine, o título «Você
 * por aqui», os pontos, a sequência, os cursos concluídos, os selos e a
 * comparação com a turma. **Duas ordens do dono, no mesmo dia, desmontaram-no:**
 *
 * 1. *«não precisa desse quadro inteiro que diz "você tem 21 pontos"»* — a
 *    comparação com a mediana e as duas barras foram apagadas;
 * 2. *«tanto a carga horária quanto esses dados deveriam estar no topo ao lado
 *    do evento da agenda»* — o que sobrou subiu para `TiraDoEstudo`, em
 *    `componentes/LinhaDoAcervo.tsx`, junto com as fatias do acervo.
 *
 * ## O argumento que caiu, e o que dele continua de pé
 *
 * Estava escrito aqui que o bloco ficava no fim porque *«ele é sobre quem está
 * olhando, e o que a pessoa veio fazer é assistir — no topo, a gamificação
 * viraria o assunto da página em vez do curso»*, e que **nunca a posição**,
 * porque *«você é o 187º de 400» informa que 186 pessoas estão à frente, e a
 * leitura é de derrota*.
 *
 * **A primeira frase caiu por ordem direta.** O que a atenua é a forma: o que
 * subiu é uma linha de texto de ~16 px, não uma caixa de 240 — a gamificação
 * não virou o assunto da página, virou uma fatia entre seis.
 *
 * **A segunda continua inteira, e não dependia deste sítio.** O servidor nunca
 * devolveu posição nenhuma — só a mediana, que agora também não é lida por tela
 * alguma —, e continua a não haver por onde exibi-la nem por engano. O placar
 * mostra `1..5` dentro de uma lista de cinco, e quem não está entre os cinco não
 * recebe linha nenhuma. *Posição na turma continua a não existir em lugar
 * nenhum deste produto.*
 *
 * A promessa de privacidade que morreu aqui em 08/09/2026, e a frase que o dono
 * pôs no lugar dela, viajaram com o bloco — ver `TiraDoEstudo`. A cerca que as
 * vigia (`a-frase-da-privacidade-nao-sobrevive-ao-placar`) passou a montar a
 * tira, e é ela que continua a provar que a frase só existe no estado em que é
 * verdadeira.
 */

/*
 * `BarraDaTurma` foi apagada aqui em 28/09/2026, com a comparação que ela
 * desenhava — ver o comentário em `BlocoDoProgresso`. Não ficou comentada nem
 * guardada «para o caso de»: um componente morto neste ficheiro, que já é o
 * mais disputado desta app, é o que volta por engano na primeira leitura
 * apressada. O git tem-na, e o commit diz por quê.
 */

/**
 * Uma trilha por dentro.
 *
 * ## O que esta página é
 *
 * Um **trajeto**: as duas pontas, as etapas na ordem que a trilha afirma, e o
 * marco de "você está aqui". Ela existe porque uma sequência de cursos precisa
 * de um endereço próprio ("comece por aqui") e de uma resposta a "e agora?", e
 * nenhuma das duas cabe numa prateleira.
 *
 * O desenho mora em `TrajetoDaTrilha`; o que fica aqui é a busca. A separação é
 * a mesma do `Cabecalho` e da `TrilhasDaArea`, e pelo mesmo motivo: um
 * componente próprio é o que permite olhar o trajeto sem carregar `App.tsx`
 * inteiro atrás.
 *
 * ## O que ela NÃO faz
 *
 * A ORDEM não tranca. Toda etapa é clicável, inclusive a quinta de quem não
 * abriu a primeira — a ordem é uma recomendação, e quem já sabe o assunto do
 * curso 1 não deve ficar preso por uma regra que não o ajuda. Isto foi decidido
 * na 0103 e continua de pé.
 *
 * ## ⚠ ESTE PARÁGRAFO DIZIA «NEM CADEADO NENHUM NA TELA», E ERRADO DUAS VEZES
 *
 * Primeiro desde a **0427** (23/09/2026): a trilha inteira passou a poder
 * esperar por um pré-requisito, e `TrilhaDetalhada.tranca` desenha cadeado e
 * motivo. O comentário ficou a descrever o produto de uma versão atrás.
 *
 * Segundo desde a **0467** (06/10/2026): a trilha virou produto e PODE
 * conceder. Quem não a alcança não chega a esta página — a rota devolve a
 * oferta e a tela desvia, como `PaginaDoCurso` já faz. O que nunca existiu, e
 * continua a não existir, é um `podeVerTrilha`: a função genérica chama-se
 * `podeVerItem`, e inventar um nome por espécie é como se chega a duas réguas.
 */
function PaginaDaTrilha() {
  const t = useT();
  const { areaSlug: slugDaRota, trilhaSlug = '' } = useParams();
  const areaSlug = useAreaSlug(slugDaRota);
  const { dados, erro } = useCarregar<TrilhaDetalhada>(
    () => api.get(`/a/${areaSlug}/trilhas/${trilhaSlug}`),
    [areaSlug, trilhaSlug],
  );

  if (erro) return <Recusa frase={erro} areaSlug={areaSlug} />;
  if (!dados) return <Moldura>{t('estado.carregando')}</Moldura>;

  return (
    <div className="min-h-screen" style={variaveisDoTema(dados.tema)}>
      {/*
        ── A BARRA DESTA PÁGINA SAÍA SEM AS PORTAS (08/10/2026) ──────────────

        O dono, com o retrato à frente: *«no menu top da página da trilha,
        devem existir os links de comunidade, agenda, minhas aulas, trilhas
        como é na vitrine da área de membros»*.

        Era `<Cabecalho titulo voltar area />` e mais nada. A barra desenhava-se
        na mesma, só mais curta, e nada falhava — a lição que
        `toda-tela-da-area-carrega-as-quatro-portas` escreveu a 01/10 sobre
        «Minhas aulas», repetida aqui porque a lista de telas daquela cerca era
        escrita à mão e esta página nasceu depois dela.
      */}
      <Cabecalho
        titulo={dados.trilha.title}
        voltar={caminhoDaArea(areaSlug)}
        area={areaSlug}
        menu={dados.menu}
        assistente={dados.assistente}
        cabecalho={dados.cabecalho ?? null}
        portas={
          <PortasDaArea
            areaSlug={areaSlug}
            temTrilhas={dados.portas?.temTrilhas ?? false}
            cracha={dados.portas?.cracha ?? null}
            /* `?? undefined` e nunca `?? false`: «não sei» não esconde a porta
               por onde toda a gente passa. Ver `PortasDaArea`. */
            comunidadeAberta={dados.portas?.comunidadeAberta ?? undefined}
          />
        }
      />
      <Suspense fallback={<Espera />}>
        <TrajetoDaTrilha dados={dados} areaSlug={areaSlug} />
      </Suspense>
    </div>
  );
}

/**
 * As âncoras que a página do curso HONRA em vez de saltar para a aula.
 *
 * Uma lista, e não dois `includes` numa condição: as duas seções só existem
 * nesta página, e quem acrescentar a terceira precisa de um lugar óbvio para a
 * pôr — senão ela nasce funcionando no link e quebrada no redirecionamento, que
 * é um defeito que só aparece para quem clica de fora.
 */
const ANCORAS_DA_LISTA = ['certificado', 'rendimento'];

export function PaginaDoCurso() {
  const t = useT();
  const { areaSlug: slugDaRota, cursoId = '' } = useParams();
  const areaSlug = useAreaSlug(slugDaRota);
  const { dados, erro, oferta } = useCarregar<CursoDetalhado>(
    () => api.get(`/a/${areaSlug}/cursos/${cursoId}`),
    [areaSlug, cursoId],
  );
  useMedicaoDaPagina(dados?.medicao);

  /* O favicon também aqui: quem cola o link de um curso chega sem passar pela
     vitrine, e a aba mostraria o ícone padrão. O título da aba é o do curso. */
  useMarca(dados?.marca, dados?.curso.title);

  /*
   * O `#certificado` do fim de curso, levado a sério.
   *
   * O navegador rola até a âncora quando a PÁGINA carrega com ela na URL. Numa
   * SPA a navegação é do roteador: o elemento ainda não existe no instante em
   * que a URL muda, e o `#certificado` não faz nada — o link cairia no topo de
   * uma página cuja altura é o curso inteiro, e a pessoa procuraria sozinha a
   * seção que o botão prometeu.
   *
   * Por isso a rolagem espera os DADOS, que é quando a seção passa a existir.
   * `dados` na dependência, e não um `setTimeout`: o alvo aparece com o
   * conteúdo, não depois de um prazo chutado.
   */
  useEffect(() => {
    const ancora = window.location.hash.replace('#', '');
    if (!dados || !ANCORAS_DA_LISTA.includes(ancora)) return;

    /*
     * Depois do PRÓXIMO quadro, e não neste.
     *
     * Medido: rolando dentro do próprio efeito, a seção parava a 610px do topo
     * de uma janela de 814 — visível, mas não no topo, e a página tinha rolado
     * só 150px. O motivo é que o efeito corre junto com a montagem da lista de
     * módulos: naquele instante o documento ainda é curto, e o navegador limita a
     * rolagem à altura que existe. Quando os módulos terminam de ocupar o espaço
     * deles, o alvo já saiu de onde tinha sido posto.
     *
     * Dois `requestAnimationFrame` porque um só ainda cai antes do layout do
     * quadro em que o conteúdo entra. É o mesmo truque de sempre, e é barato:
     * dois quadros são ~32ms, e não há laço nem prazo chutado.
     */
    let quadro = 0;
    const alvo = () => document.getElementById(ancora);
    quadro = requestAnimationFrame(() => {
      quadro = requestAnimationFrame(() => alvo()?.scrollIntoView({ block: 'start' }));
    });
    return () => cancelAnimationFrame(quadro);
  }, [dados]);

  /* O curso existe e não é seu — com ou sem para onde mandar. */
  if (oferta)
    return <SemAcessoAoCurso url={oferta.url} titulo={oferta.titulo} areaSlug={areaSlug} />;
  if (erro) return <Recusa frase={erro} areaSlug={areaSlug} />;
  if (!dados) return <Moldura>{t('estado.carregando')}</Moldura>;

  /*
   * O CURSO ABRE NA AULA (04/09/2026).
   *
   * O dono: «a tela que lista as aulas quando clica num curso já deveria
   * abrir na tela das aulas, na última aula que o usuário parou; se é a
   * primeira vez, na primeira aula do primeiro módulo». O servidor decide qual
   * (`abrirEm`, pela mesma régua da prateleira da vitrine), e esta página só
   * obedece — com `replace`, para o «voltar» do navegador não cair aqui e ser
   * mandado de novo à aula.
   *
   * A lista continua existindo para três coisas: o `#certificado` (o fim do
   * curso e a tela de parabéns mandam para cá de propósito), o `#rendimento`
   * (o boletim, para onde a prova manda quem quer ver as suas notas) e o curso
   * em que nenhuma aula está liberada (`abrirEm: null`), onde os cadeados são a
   * resposta.
   *
   * As duas âncoras são a MESMA regra, e por isso moram numa lista: um segundo
   * `includes` colado com `||` é o que faz a terceira âncora, um dia, ser
   * esquecida aqui e cair na aula sem que ninguém entenda porquê.
   */
  /*
   * ── O CURSO QUE ESPERA OUTRO PARA O REDIRECIONAMENTO (24/09/2026) ────────
   *
   * Com tranca, mandar para a aula é mandar para o 403: `exigirAula` recusa o
   * corpo, e a pessoa recebe a frase certa **depois** do clique que ela nem deu.
   * O curso trancado fica nesta página, que passa a ser um índice — ver
   * `AvisoDoCursoTrancado`, que explica as duas decisões do desenho.
   *
   * `lerTranca` e não o campo cru: o `dist` do aluno e a API podem ser de levas
   * diferentes por uns minutos, e a página inteira não pode cair por causa de um
   * campo em falta.
   */
  /*
   * A DATA DE ESTREIA faz o mesmo, e manda na dependência quando há as duas —
   * ver `CapaDeCurso`, na vitrine, que explica a ordem: antes do dia marcado
   * não há gesto que adiante nada, e a régua da dependência chegaria cedo
   * demais. Uma voz por cartão, uma voz por página.
   */
  const lancamento = lerLancamento(dados.curso.lancamento);
  const trancaDoCurso = lancamento ? null : lerTranca(dados.curso.tranca);
  /* Os simulados do curso, uma vez — o boletim e a régua do certificado leem
     os dois a mesma lista, que é o que os impede de discordar. */
  const simulados = idsDeSimulado(dados.modulos);

  if (
    dados.abrirEm &&
    !trancaDoCurso &&
    !lancamento &&
    !ANCORAS_DA_LISTA.some((a) => window.location.hash.includes(a))
  ) {
    return <Navigate replace to={caminhoDaArea(areaSlug, `/aula/${dados.abrirEm.lessonId}`)} />;
  }

  return (
    <div className="min-h-screen" style={variaveisDoTema(dados.tema)}>
      {/* O mesmo buraco da página da trilha, e pela mesma causa. Não foi
          pedido para esta tela — a ordem foi sobre a trilha —, mas a lei que a
          cerca escreveu a 01/10 é «toda tela da área», e fechar uma e deixar a
          irmã aberta é esperar a terceira queixa sobre a mesma coisa. */}
      <Cabecalho
        titulo={dados.curso.title}
        voltar={caminhoDaArea(areaSlug)}
        area={areaSlug}
        menu={dados.menu}
        assistente={dados.assistente}
        cabecalho={dados.cabecalho ?? null}
        portas={
          <PortasDaArea
            areaSlug={areaSlug}
            temTrilhas={dados.portas?.temTrilhas ?? false}
            cracha={dados.portas?.cracha ?? null}
            /* `?? undefined` e nunca `?? false`: «não sei» não esconde a porta
               por onde toda a gente passa. Ver `PortasDaArea`. */
            comunidadeAberta={dados.portas?.comunidadeAberta ?? undefined}
          />
        }
      />
      <div className="mx-auto max-w-3xl px-6 pb-16">
        {/* ANTES da descrição: quem chegou por link direto precisa de saber o
            que esta página é antes de começar a lê-la. */}
        {trancaDoCurso && <AvisoDoCursoTrancado tranca={trancaDoCurso} areaSlug={areaSlug} />}
        {lancamento && <AvisoDoLancamento lancamento={lancamento} />}
        {dados.curso.description && (
          <p className="pt-4" style={{ color: 'var(--texto-suave)' }}>
            {dados.curso.description}
          </p>
        )}
        {dados.modulos.map((m, i) => (
          <Modulo
            key={m.id}
            modulo={m}
            areaSlug={areaSlug}
            ordem={dados.ordem}
            numero={String(i + 1)}
            inerte={Boolean(trancaDoCurso || lancamento)}
          />
        ))}

        {/* O boletim antes do certificado: ele responde «como eu vou», e o
            certificado responde «já posso levar o papel» — a segunda pergunta
            só se faz depois da primeira. */}
        <Suspense fallback={<Espera />}>
          <RendimentoDoCurso
            areaSlug={areaSlug}
            avaliacoes={dados.avaliacoes}
            media={dados.mediaDasAvaliacoes}
            /* A espécie sai da ÁRVORE, que a mesma resposta já traz: `avaliacoes`
             é uma lista à parte, indexada por `aulaId`. Derivar daqui evita um
             segundo campo a dizer a mesma coisa — e o dia em que os dois
             divergissem seria o dia em que a média e o boletim discordariam
             sobre a mesma prova. */
            simulados={simulados}
          />
        </Suspense>

        {dados.curso.certificateEnabled && (
          <Suspense fallback={<Espera />}>
            <Certificado
              areaSlug={areaSlug}
              cursoId={dados.curso.id}
              emitido={dados.certificado}
              percent={dados.progresso.percent}
              regra={dados.curso}
              /*
               * O SIMULADO NÃO CHEGA À RÉGUA DO CERTIFICADO (0431).
               *
               * `podeEmitirCertificado` é a mesma função da rota de emissão, e
               * ela exige aprovação em toda avaliação da lista que recebe. Com
               * um simulado dentro, esta tela escreveria «falta passar em
               * Simulado 1» — a tela a afirmar que o treino decide, que é
               * exactamente o que o motor garante que não. O servidor pode já
               * o filtrar; filtrar aqui não custa nada e não depende disso.
               */
              avaliacoes={dados.avaliacoes.filter((a) => !simulados.has(a.aulaId))}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}

/* Exportada pela mesma razão de `PaginaDoCurso`: a cerca da recusa monta-a
   direto, sem o roteador inteiro. Ver `a-recusa-da-aula-desenha-a-distancia`. */
export function PaginaDaAula() {
  const t = useT();
  const { areaSlug: slugDaRota, aulaId = '' } = useParams();
  /* O `?t=` da citação do assessor (F12). Lido uma vez por navegação. */
  const { search: busca } = useLocation();
  const areaSlug = useAreaSlug(slugDaRota);
  /*
   * Sem `recarregar`, e é deliberado.
   *
   * A aula era rebuscada a cada conclusão, e rebuscar desmonta a sala inteira —
   * o player some e volta, a posição do vídeo se perde e o contador de avanço
   * morre no mesmo clique que o criou. Quem muda de estado ao concluir é a
   * própria `SalaDeAula`, que rebusca só o sumário. Ver N11 e o comentário de
   * `concluida`, lá dentro.
   */
  const { dados, erro, tranca } = useCarregar<Aula>(
    () => api.get(`/a/${areaSlug}/aulas/${aulaId}`),
    [areaSlug, aulaId],
  );

  /*
   * A aula trancada tem SAÍDA (0282).
   *
   * A frase sozinha numa página preta — sem cabeçalho, sem seta, sem lista —
   * era o que o testador viu: «o aluno fica sem saída além do botão voltar do
   * navegador». A frase diz o que abre; os dois links levam até lá e de volta.
   */
  if (tranca) {
    /*
     * ── QUANDO QUEM NÃO ABRIU É O CURSO, E NÃO ESTA AULA (24/09/2026) ──────
     *
     * Com `motivo: 'hora_marcada'` a recusa muda **o sujeito da primeira
     * linha**. «Esta aula ainda não abriu» sobre um curso que ainda não estreou
     * manda a pessoa procurar as outras aulas — e nenhuma abre, porque enquanto
     * o curso não abriu nada dentro dele abriu. Nomear o objeto errado custa
     * uma volta inteira pela lista antes de a pessoa entender.
     *
     * E a frase é **refeita aqui**: a do 403 vem contada no relógio do
     * servidor, que é UTC, e perto da meia-noite diz o dia errado para quem lê
     * noutro fuso. Ver `fraseDaEspera`.
     */
    const agora = new Date();
    const espera = tranca.lancamento;
    const falta = espera ? quantoFalta(diasAte(espera.em, agora)) : null;
    return (
      <Moldura>
        <p className="text-sm" style={{ color: 'var(--texto-suave)' }}>
          {espera ? t('estado.cursoNaoAbriu') : t('estado.aulaNaoAbriu')}
        </p>
        <p className="mt-2 text-lg font-medium">
          {espera ? fraseDaEspera(espera, agora, t) : tranca.frase}
          {falta && (
            <span className="ml-2 text-sm font-normal" style={{ color: 'var(--texto-suave)' }}>
              · {falta}
            </span>
          )}
        </p>
        {/*
         * A régua, e não uma segunda frase (24/09/2026).
         *
         * A frase acima é a do servidor e é o título desta tela; o que falta a
         * quem chega aqui é a DISTÂNCIA — «pede 60%» sem «você está em 25%»
         * desenhado deixa a pessoa a adivinhar se está perto. `ReguasDaTranca`
         * é a mesma peça dos cartões, e por isso a recusa e a vitrine mostram a
         * mesma medida da mesma porta.
         */}
        {tranca.dependencia && (
          <div className="mt-3 max-w-md">
            <ReguasDaTranca tranca={tranca.dependencia} />
          </div>
        )}
        <div className="mt-6 flex flex-col gap-2">
          {/* A porta para o que destranca, com a mesma forma do botão da
              avaliação: os dois respondem «e agora, o quê?», e a recusa que não
              responde isso é uma parede com legenda. */}
          {tranca.dependencia && destinoDaTranca(tranca.dependencia) && (
            <Link
              to={caminhoDaArea(areaSlug, destinoDaTranca(tranca.dependencia)!)}
              className="inline-flex min-h-[44px] items-center self-start px-5 py-2.5 text-sm font-medium"
              style={{
                background: 'var(--destaque)',
                color: 'var(--sobre-destaque)',
                borderRadius: 'var(--raio)',
              }}
            >
              {t('tranca.irPara', { titulo: tranca.dependencia.titulo })} →
            </Link>
          )}
          {tranca.avaliacao && (
            <Link
              to={caminhoDaArea(areaSlug, `/aula/${tranca.avaliacao.id}`)}
              className="inline-flex min-h-[44px] items-center self-start px-5 py-2.5 text-sm font-medium"
              style={{
                background: 'var(--destaque)',
                color: 'var(--sobre-destaque)',
                borderRadius: 'var(--raio)',
              }}
            >
              {t('prova.irParaAvaliacao')} →
            </Link>
          )}
          <Link
            to={caminhoDaArea(areaSlug, '/')}
            className="inline-flex min-h-[44px] items-center self-start text-sm underline"
            style={{ color: 'var(--texto-suave)' }}
          >
            ← {t('nav.voltarAoInicio')}
          </Link>
        </div>
      </Moldura>
    );
  }

  if (erro) {
    /* «Aula não encontrada» numa página preta sem saída era o que o testador
       viu. A frase fica; a porta de volta é o que faltava. */
    return (
      <Moldura>
        <p className="text-lg font-medium">{erro}</p>
        <Link
          to={caminhoDaArea(areaSlug, '/')}
          className="mt-6 inline-flex min-h-[44px] items-center self-start text-sm underline"
          style={{ color: 'var(--texto-suave)' }}
        >
          ← {t('nav.voltarAoInicio')}
        </Link>
      </Moldura>
    );
  }
  if (!dados) return <Moldura>{t('estado.carregando')}</Moldura>;

  /*
   * A sala de aula mora num componente próprio, e o `Cabecalho` da área saiu.
   *
   * Saiu porque ele mostrava o nome do CURSO no lugar reservado ao nome da área,
   * e a sala agora tem barra própria — com o curso, o progresso e o botão da
   * lista. Duas barras empilhadas dizendo a mesma coisa custariam 100px da única
   * tela em que a altura importa: a que tem um vídeo dentro.
   */
  return (
    <div className="min-h-screen" style={variaveisDoTema(dados.tema)}>
      <Suspense
        fallback={
          <p className="px-6 pt-10 text-sm" style={{ color: 'var(--texto-suave)' }}>
            {t('estado.carregando')}
          </p>
        }
      >
        {/* A cerca por AULA: o erro de uma não fica preso na seguinte. */}
        <CercaDeErro key={aulaId} oQue="cerca.estaAula">
          <SalaDeAula
            dados={dados}
            areaSlug={areaSlug}
            aulaId={aulaId}
            /* F12: o `?t=` do link fundo (a citação do assessor, o `destino` do
               acervo) vence a retomada; fora de `[0, duração]`, a retomada decide.
               A regra é pura e testada em `lib/posicao-inicial.ts`. */
            retomarEm={posicaoInicial({
              t: new URLSearchParams(busca).get('t'),
              retomar: dados.progresso.retomarEm,
              duracao: dados.aula.durationSeconds ?? dados.progresso.duracao,
            })}
          />
        </CercaDeErro>
      </Suspense>
    </div>
  );
}

/**
 * Um módulo do curso, e os submódulos dele.
 *
 * ## A numeração vem do servidor, e é uma só
 *
 * `ordem` é a lista de ids na sequência de leitura, calculada por
 * `ordemLinearDasAulas` — a mesma função que o painel usa para numerar o
 * sumário. Numerar aqui por conta própria (o índice dentro do módulo) faria a
 * aula 14 do painel ser a 2 do aluno, e nenhum dos dois estaria "errado": eles
 * estariam contando coisas diferentes.
 *
 * O número é contínuo no curso inteiro porque é isso que ele significa — a aula
 * 14 é a décima quarta coisa que se assiste, não a segunda do quarto módulo.
 */
/**
 * Uma aula na lista do curso — aberta ou trancada.
 *
 * ## Por que a trancada não é um `<Link>` desabilitado
 *
 * Porque link desabilitado não existe: `<a>` sem `href` sai da ordem de
 * tabulação e o leitor de tela o anuncia como texto, o que aqui é a verdade —
 * não há para onde ir. O `<div>` diz a mesma coisa sem fingir. `aria-disabled`
 * num link que continua clicável seria pior que os dois.
 *
 * A linha inteira fica esmaecida menos a frase: o que a pessoa precisa ler é
 * *quando*, não *que não pode*.
 */
function LinhaDaAula({
  aula,
  areaSlug,
  numero,
  /**
   * O CURSO inteiro espera outro (24/09/2026), e nenhuma linha é link.
   *
   * Não é o mesmo que `aula.bloqueada`, e por isso é outro campo: ali a
   * **linha** está fechada (pela data ou pela avaliação do módulo) e diz quando
   * abre; aqui a linha está aberta e é a **porta da rua** que não está. A
   * distinção importa no desenho: a inerte não ganha cadeado nem frase própria
   * — o motivo já está dito uma vez, no topo da página, e repeti-lo em trinta
   * linhas seria papel de parede.
   */
  inerte = false,
}: {
  aula: AulaDaLista;
  areaSlug: string;
  numero: number;
  inerte?: boolean;
}) {
  const t = useT();
  const locale = useLocale();
  const miolo = (
    <>
      {/*
       * A miniatura, quando existe.
       *
       * Uma lista de trinta aulas em texto puro é uma lista que ninguém
       * percorre. `loading="lazy"` porque um curso longo carregaria trinta
       * imagens assinadas de uma vez no 4G.
       */}
      {aula.capa && (
        <img
          src={aula.capa}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-9 w-16 shrink-0 object-cover"
          style={{ borderRadius: 'calc(var(--raio) / 2)', background: 'var(--superficie)' }}
        />
      )}
      {/* Trancada = cadeado, e não um ponto dentro do mesmo círculo da não
          assistida: um `·` de 11px contra o vazio é diferença que ninguém vê.
          Mesma decisão, mesmo desenho e mesmo motivo de `MarcaDoEstado`. */}
      {aula.bloqueada ? (
        <span
          data-vst-marca="trancada"
          className="grid h-5 w-5 shrink-0 place-items-center"
          style={{ color: 'var(--texto-suave)' }}
        >
          <GlifoDeCadeado tamanho={14} />
        </span>
      ) : (
        <span
          data-vst-marca={aula.progresso.concluida ? 'concluida' : 'aberta'}
          className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px]"
          style={{
            background: aula.progresso.concluida ? 'var(--destaque)' : 'transparent',
            border: aula.progresso.concluida ? 'none' : '1px solid var(--texto-suave)',
            color: 'var(--sobre-destaque)',
          }}
        >
          {aula.progresso.concluida ? '✓' : ''}
        </span>
      )}
      <span className="shrink-0 text-xs tabular-nums" style={{ color: 'var(--texto-suave)' }}>
        {numero}
      </span>
      <span className="flex-1">{aula.title}</span>
      {/*
       * ── A REGRA REVOGADA: o tipo aparece em TODA linha ──────────────────
       *
       * Aqui vivia `ROTULO_DO_TIPO`, uma lista curta de cinco tipos, com esta
       * justificativa: «vídeo e texto são a esmagadora maioria e não ganham
       * etiqueta: marcar todos transformaria a coluna em papel de parede».
       *
       * O argumento estava certo sobre PALAVRAS na borda direita e errado sobre
       * a categoria em si. Uma palavra repetida cinquenta vezes custa uma
       * fixação por linha e vira ruído; uma silhueta numa coluna fixa vira
       * TEXTURA — o olho a percorre de graça e só para quando ela muda. E o
       * preço da regra era alto: com `video` e `texto` somando a maioria das
       * linhas, a coluna ficava calada justamente na maioria dos casos, que é a
       * queixa que o dono trouxe em 06/09/2026.
       *
       * O que fica da regra antiga é o cuidado que a motivou: a palavra não
       * volta para a borda direita solta — ela vem colada ao desenho, formando
       * uma peça só.
       */}
      <span
        className="flex shrink-0 items-center gap-1.5 text-[11px]"
        style={{ color: 'var(--texto-suave)' }}
      >
        <GlifoDoTipo kind={aula.kind} simulado={aula.simulado} tamanho={14} />
        {/* A palavra NÃO some no celular: um desenho sozinho não é achável por
            busca nem lido por leitor de tela, e «PDF» tem três letras.

            «Simulado» escreve-se aqui, no lugar de «Avaliação», e não ganha
            uma segunda etiqueta: é a mesma pergunta — o que é isto — e por
            isso é o mesmo encaixe (ver `rotuloDoTipo`). O «pode pular» NÃO
            vem para esta página: aqui a linha é um link para a aula, e a folha
            dela diz a dispensa por extenso, com o gesto ao lado. Na sala, que
            é o índice sempre aberto, a promessa está escrita. */}
        <span>{rotuloDoTipo(aula.kind, aula.simulado)}</span>
      </span>
    </>
  );

  const fundo = { background: 'var(--superficie)', borderRadius: 'var(--raio)' };

  if (aula.bloqueada) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 opacity-60" style={fundo}>
        {miolo}
        <span className="shrink-0 text-xs opacity-100" style={{ color: 'var(--destaque)' }}>
          {fraseDoCadeado(aula.liberaEm, aula.frase, t, locale)}
        </span>
      </div>
    );
  }

  /* A linha do curso trancado: o MESMO miolo, a mesma duração, sem `<Link>` e
     sem esmaecer. Ela não está fechada — está à espera —, e esmaecê-la diria
     «você não pode» trinta vezes numa página que já disse «ainda não» uma. */
  const corpo = (
    <>
      {miolo}
      <span className="text-xs" style={{ color: 'var(--texto-suave)' }}>
        {duracaoLegivel(aula.durationSeconds)}
      </span>
    </>
  );

  if (inerte) {
    return (
      <div className="flex items-center gap-3 px-4 py-3" style={fundo}>
        {corpo}
      </div>
    );
  }

  return (
    <Link
      to={caminhoDaArea(areaSlug, `/aula/${aula.id}`)}
      className="flex items-center gap-3 px-4 py-3"
      style={fundo}
    >
      {corpo}
    </Link>
  );
}

/**
 * Um módulo da página do curso — e a MESMA gramática de três postos da sala.
 *
 * A página do curso tinha a doença da lista lateral em outra dose: módulo e
 * submódulo diferiam por `text-lg` contra `text-sm` e 16px de recuo, e nenhum
 * dos dois dizia o que era. Aqui vale o que `ListaDaSala` documenta por extenso:
 * a **palavra do posto** na sobrancelha resolve sozinha, sem legenda e sem cor.
 *
 * O que NÃO viaja para cá é o trilho: esta é uma página larga com as aulas em
 * cartões preenchidos, e um fio vertical ao lado de cartões não delimita nada —
 * o cartão já tem borda. O trilho é da coluna estreita, onde as linhas são
 * transparentes e precisam de algo que as agrupe.
 */
function Modulo({
  modulo,
  areaSlug,
  ordem,
  nivel = 0,
  numero,
  /** O curso espera outro: a lista fica inteira e nenhuma linha é link. */
  inerte = false,
}: {
  modulo: ModuloDoCurso;
  areaSlug: string;
  ordem: string[];
  nivel?: number;
  /** A coordenada no sumário: `3` na raiz, `3.2` num filho. Ver `ListaDaSala`. */
  numero: string;
  inerte?: boolean;
}) {
  const ehRaiz = nivel === 0;
  return (
    <section className={ehRaiz ? 'pt-8' : 'pt-5'} style={{ paddingLeft: nivel * 16 }}>
      <span
        className="block text-[10px] font-semibold uppercase tracking-[0.14em]"
        style={{ color: 'var(--texto-suave)' }}
      >
        {ehRaiz ? 'Módulo' : 'Submódulo'} {numero}
      </span>
      <h2 className={ehRaiz ? 'text-lg font-semibold tracking-[-0.01em]' : 'text-sm font-semibold'}>
        {modulo.title}
      </h2>

      {modulo.aulas.length > 0 && (
        <ul className="mt-3 flex flex-col gap-1">
          {modulo.aulas.map((a) => (
            <li key={a.id}>
              <LinhaDaAula
                aula={a}
                areaSlug={areaSlug}
                numero={ordem.indexOf(a.id) + 1}
                inerte={inerte}
              />
            </li>
          ))}
        </ul>
      )}

      {modulo.submodulos.map((s, i) => (
        <Modulo
          key={s.id}
          modulo={s}
          areaSlug={areaSlug}
          ordem={ordem}
          nivel={nivel + 1}
          numero={`${numero}.${i + 1}`}
          /* Sem isto o submódulo voltaria a ser link no curso trancado — a
             lista mais funda é exatamente onde ninguém repara. */
          inerte={inerte}
        />
      ))}
    </section>
  );
}
